import axios from 'axios';
import { ComputeBudgetProgram, PublicKey, VersionedTransaction, TransactionMessage, Keypair, SystemProgram, TransactionInstruction } from '@solana/web3.js';
import nacl from 'tweetnacl';
import * as anchor from '@coral-xyz/anchor';
import { Program } from '@coral-xyz/anchor';
import BN from 'bn.js';
import ReconnectingWebSocket from 'reconnecting-websocket';

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var isRetryAllowed$1;
var hasRequiredIsRetryAllowed;

function requireIsRetryAllowed () {
	if (hasRequiredIsRetryAllowed) return isRetryAllowed$1;
	hasRequiredIsRetryAllowed = 1;

	const denyList = new Set([
		'ENOTFOUND',
		'ENETUNREACH',

		// SSL errors from https://github.com/nodejs/node/blob/fc8e3e2cdc521978351de257030db0076d79e0ab/src/crypto/crypto_common.cc#L301-L328
		'UNABLE_TO_GET_ISSUER_CERT',
		'UNABLE_TO_GET_CRL',
		'UNABLE_TO_DECRYPT_CERT_SIGNATURE',
		'UNABLE_TO_DECRYPT_CRL_SIGNATURE',
		'UNABLE_TO_DECODE_ISSUER_PUBLIC_KEY',
		'CERT_SIGNATURE_FAILURE',
		'CRL_SIGNATURE_FAILURE',
		'CERT_NOT_YET_VALID',
		'CERT_HAS_EXPIRED',
		'CRL_NOT_YET_VALID',
		'CRL_HAS_EXPIRED',
		'ERROR_IN_CERT_NOT_BEFORE_FIELD',
		'ERROR_IN_CERT_NOT_AFTER_FIELD',
		'ERROR_IN_CRL_LAST_UPDATE_FIELD',
		'ERROR_IN_CRL_NEXT_UPDATE_FIELD',
		'OUT_OF_MEM',
		'DEPTH_ZERO_SELF_SIGNED_CERT',
		'SELF_SIGNED_CERT_IN_CHAIN',
		'UNABLE_TO_GET_ISSUER_CERT_LOCALLY',
		'UNABLE_TO_VERIFY_LEAF_SIGNATURE',
		'CERT_CHAIN_TOO_LONG',
		'CERT_REVOKED',
		'INVALID_CA',
		'PATH_LENGTH_EXCEEDED',
		'INVALID_PURPOSE',
		'CERT_UNTRUSTED',
		'CERT_REJECTED',
		'HOSTNAME_MISMATCH'
	]);

	// TODO: Use `error?.code` when targeting Node.js 14
	isRetryAllowed$1 = error => !denyList.has(error && error.code);
	return isRetryAllowed$1;
}

var isRetryAllowedExports = requireIsRetryAllowed();
var isRetryAllowed = /*@__PURE__*/getDefaultExportFromCjs(isRetryAllowedExports);

const namespace = 'axios-retry';
function isNetworkError(error) {
    const CODE_EXCLUDE_LIST = ['ERR_CANCELED', 'ECONNABORTED'];
    if (error.response) {
        return false;
    }
    if (!error.code) {
        return false;
    }
    // Prevents retrying timed out & cancelled requests
    if (CODE_EXCLUDE_LIST.includes(error.code)) {
        return false;
    }
    // Prevents retrying unsafe errors
    return isRetryAllowed(error);
}
const SAFE_HTTP_METHODS = ['get', 'head', 'options'];
const IDEMPOTENT_HTTP_METHODS = SAFE_HTTP_METHODS.concat(['put', 'delete']);
function isRetryableError(error) {
    return (error.code !== 'ECONNABORTED' &&
        (!error.response ||
            error.response.status === 429 ||
            (error.response.status >= 500 && error.response.status <= 599)));
}
function isSafeRequestError(error) {
    if (!error.config?.method) {
        // Cannot determine if the request can be retried
        return false;
    }
    return isRetryableError(error) && SAFE_HTTP_METHODS.indexOf(error.config.method) !== -1;
}
function isIdempotentRequestError(error) {
    if (!error.config?.method) {
        // Cannot determine if the request can be retried
        return false;
    }
    return isRetryableError(error) && IDEMPOTENT_HTTP_METHODS.indexOf(error.config.method) !== -1;
}
function isNetworkOrIdempotentRequestError(error) {
    return isNetworkError(error) || isIdempotentRequestError(error);
}
function retryAfter(error = undefined) {
    const retryAfterHeader = error?.response?.headers['retry-after'];
    if (!retryAfterHeader) {
        return 0;
    }
    // if the retry after header is a number, convert it to milliseconds
    let retryAfterMs = (Number(retryAfterHeader) || 0) * 1000;
    // If the retry after header is a date, get the number of milliseconds until that date
    if (retryAfterMs === 0) {
        retryAfterMs = (new Date(retryAfterHeader).valueOf() || 0) - Date.now();
    }
    return Math.max(0, retryAfterMs);
}
function noDelay(_retryNumber = 0, error = undefined) {
    return Math.max(0, retryAfter(error));
}
function exponentialDelay(retryNumber = 0, error = undefined, delayFactor = 100) {
    const calculatedDelay = 2 ** retryNumber * delayFactor;
    const delay = Math.max(calculatedDelay, retryAfter(error));
    const randomSum = delay * 0.2 * Math.random(); // 0-20% of the delay
    return delay + randomSum;
}
/**
 * Linear delay
 * @param {number | undefined} delayFactor - delay factor in milliseconds (default: 100)
 * @returns {function} (retryNumber: number, error: AxiosError | undefined) => number
 */
function linearDelay(delayFactor = 100) {
    return (retryNumber = 0, error = undefined) => {
        const delay = retryNumber * delayFactor;
        return Math.max(delay, retryAfter(error));
    };
}
const DEFAULT_OPTIONS = {
    retries: 3,
    retryCondition: isNetworkOrIdempotentRequestError,
    retryDelay: noDelay,
    shouldResetTimeout: false,
    onRetry: () => { },
    onMaxRetryTimesExceeded: () => { },
    validateResponse: null
};
function getRequestOptions(config, defaultOptions) {
    return { ...DEFAULT_OPTIONS, ...defaultOptions, ...config[namespace] };
}
function setCurrentState(config, defaultOptions, resetLastRequestTime = false) {
    const currentState = getRequestOptions(config, defaultOptions || {});
    currentState.retryCount = currentState.retryCount || 0;
    if (!currentState.lastRequestTime || resetLastRequestTime) {
        currentState.lastRequestTime = Date.now();
    }
    config[namespace] = currentState;
    return currentState;
}
function fixConfig(axiosInstance, config) {
    // @ts-ignore
    if (axiosInstance.defaults.agent === config.agent) {
        // @ts-ignore
        delete config.agent;
    }
    if (axiosInstance.defaults.httpAgent === config.httpAgent) {
        delete config.httpAgent;
    }
    if (axiosInstance.defaults.httpsAgent === config.httpsAgent) {
        delete config.httpsAgent;
    }
}
async function shouldRetry(currentState, error) {
    const { retries, retryCondition } = currentState;
    const shouldRetryOrPromise = (currentState.retryCount || 0) < retries && retryCondition(error);
    // This could be a promise
    if (typeof shouldRetryOrPromise === 'object') {
        try {
            const shouldRetryPromiseResult = await shouldRetryOrPromise;
            // keep return true unless shouldRetryPromiseResult return false for compatibility
            return shouldRetryPromiseResult !== false;
        }
        catch (_err) {
            return false;
        }
    }
    return shouldRetryOrPromise;
}
async function handleRetry(axiosInstance, currentState, error, config) {
    currentState.retryCount += 1;
    const { retryDelay, shouldResetTimeout, onRetry } = currentState;
    const delay = retryDelay(currentState.retryCount, error);
    // Axios fails merging this configuration to the default configuration because it has an issue
    // with circular structures: https://github.com/mzabriskie/axios/issues/370
    fixConfig(axiosInstance, config);
    if (!shouldResetTimeout && config.timeout && currentState.lastRequestTime) {
        const lastRequestDuration = Date.now() - currentState.lastRequestTime;
        const timeout = config.timeout - lastRequestDuration - delay;
        if (timeout <= 0) {
            return Promise.reject(error);
        }
        config.timeout = timeout;
    }
    config.transformRequest = [(data) => data];
    await onRetry(currentState.retryCount, error, config);
    if (config.signal?.aborted) {
        return Promise.resolve(axiosInstance(config));
    }
    return new Promise((resolve) => {
        const abortListener = () => {
            clearTimeout(timeout);
            resolve(axiosInstance(config));
        };
        const timeout = setTimeout(() => {
            resolve(axiosInstance(config));
            if (config.signal?.removeEventListener) {
                config.signal.removeEventListener('abort', abortListener);
            }
        }, delay);
        if (config.signal?.addEventListener) {
            config.signal.addEventListener('abort', abortListener, { once: true });
        }
    });
}
async function handleMaxRetryTimesExceeded(currentState, error) {
    if (currentState.retryCount >= currentState.retries)
        await currentState.onMaxRetryTimesExceeded(error, currentState.retryCount);
}
const axiosRetry = (axiosInstance, defaultOptions) => {
    const requestInterceptorId = axiosInstance.interceptors.request.use((config) => {
        setCurrentState(config, defaultOptions, true);
        if (config[namespace]?.validateResponse) {
            // by setting this, all HTTP responses will be go through the error interceptor first
            config.validateStatus = () => false;
        }
        return config;
    });
    const responseInterceptorId = axiosInstance.interceptors.response.use(null, async (error) => {
        const { config } = error;
        // If we have no information to retry the request
        if (!config) {
            return Promise.reject(error);
        }
        const currentState = setCurrentState(config, defaultOptions);
        if (error.response && currentState.validateResponse?.(error.response)) {
            // no issue with response
            return error.response;
        }
        if (await shouldRetry(currentState, error)) {
            return handleRetry(axiosInstance, currentState, error, config);
        }
        await handleMaxRetryTimesExceeded(currentState, error);
        return Promise.reject(error);
    });
    return { requestInterceptorId, responseInterceptorId };
};
// Compatibility with CommonJS
axiosRetry.isNetworkError = isNetworkError;
axiosRetry.isSafeRequestError = isSafeRequestError;
axiosRetry.isIdempotentRequestError = isIdempotentRequestError;
axiosRetry.isNetworkOrIdempotentRequestError = isNetworkOrIdempotentRequestError;
axiosRetry.exponentialDelay = exponentialDelay;
axiosRetry.linearDelay = linearDelay;
axiosRetry.isRetryableError = isRetryableError;

let axiosClient;
async function getAxiosAuthClient() {
    if (!axiosClient) {
        const config = await getConfig();
        axiosClient = axios.create({
            baseURL: config.authApiUrl,
            headers: {
                'Content-Type': 'application/json',
            },
            timeout: 30000, // 30s timeout, matching makeApiRequest
        });
    }
    return axiosClient;
}
async function genAuthNonce() {
    const client = await getAxiosAuthClient();
    const config = await getConfig();
    const appId = config.appId;
    const response = await client.post('/auth/nonce', {
        appId
    });
    return response.data.nonce;
}
async function createSessionWithSignature(address, message, signature) {
    const client = await getAxiosAuthClient();
    const config = await getConfig();
    const appId = config.appId;
    const response = await client.post('/session', {
        appId,
        address,
        message,
        signature,
        authMethod: config.authMethod
    });
    return response.data;
}
async function createSessionWithPrivy(authToken, address, privyIdToken) {
    var _a;
    // Block in true SSR/Node.js. Allow in browser (window exists) and React Native (no window,
    // but not Node — detected via process.versions.node). Node 21+ defines navigator globally,
    // so we can't rely on navigator alone.
    const isNode = typeof process !== 'undefined' && !!((_a = process.versions) === null || _a === void 0 ? void 0 : _a.node);
    if (typeof window === 'undefined' && isNode)
        return;
    const client = await getAxiosAuthClient();
    const config = await getConfig();
    const appId = config.appId;
    const response = await client.post('/session', {
        appId,
        // createSessionWithPrivy is, by definition, the Privy session path, so the
        // server must receive authMethod:'privy' (its branch validates authToken+
        // idToken+appId). Do NOT read config.authMethod here: on a Phantom-primary app
        // with the staged Privy email swap, config.authMethod is still 'phantom' at this
        // point (commitPrivy runs after login() resolves), which routes the request to
        // the signature/message branch and fails. Hardcoding 'privy' also correctly
        // covers 'privy-expo', which the backend expects mapped to 'privy'.
        authMethod: 'privy',
        authToken,
        idToken: privyIdToken,
        address
    });
    return response.data;
}
// Deduplicate concurrent refreshSession calls to prevent multiple code paths
// (WebSocket reconnection, periodic refresh, API 401 retry) from all hitting
// /session/refresh simultaneously.
// Note: module-level state is shared across requests in SSR/Node — acceptable
// since there is only one active session per server instance (same pattern as
// refreshInFlight in api.ts).
let refreshInFlight$1 = null;
let refreshInFlightToken = null;
async function refreshSession(refreshToken) {
    if (refreshInFlight$1 && refreshInFlightToken === refreshToken) {
        return refreshInFlight$1;
    }
    refreshInFlightToken = refreshToken;
    let currentFlight;
    currentFlight = refreshInFlight$1 = (async () => {
        try {
            const client = await getAxiosAuthClient();
            const config = await getConfig();
            const appId = config.appId;
            const response = await client.post('/session/refresh', {
                refreshToken,
                appId
            });
            return response.data;
        }
        finally {
            // Only clear if we're still the active in-flight request.
            // A second call with a different token may have replaced us.
            if (refreshInFlight$1 === currentFlight) {
                refreshInFlight$1 = null;
                refreshInFlightToken = null;
            }
        }
    })();
    return refreshInFlight$1;
}
async function signSessionCreateMessage(_signMessageFunction) {
}

class WebSessionManager {
    /**
     * Decode a base64url-encoded string (used by JWT payloads).
     * Normalises base64url → standard base64 before calling browser atob().
     */
    static decodeBase64Url(input) {
        let b64 = input.replace(/-/g, '+').replace(/_/g, '/');
        const pad = b64.length % 4;
        if (pad === 2)
            b64 += '==';
        else if (pad === 3)
            b64 += '=';
        return atob(b64);
    }
    /* ------------------------------------------------------------------ */
    /* STORE                                                              */
    /* ------------------------------------------------------------------ */
    static async storeSession(address, accessToken, idToken, refreshToken) {
        if (typeof window === "undefined")
            return;
        // JWT-wallet binding: refuse to store a session whose idToken is bound
        // to a different wallet than `address`. Prevents races that would otherwise
        // leave localStorage with mismatched address/token state.
        try {
            const payloadB64 = idToken.split(".")[1];
            if (payloadB64) {
                const payload = JSON.parse(this.decodeBase64Url(payloadB64));
                const tokenWallet = payload["custom:walletAddress"];
                if (tokenWallet && tokenWallet !== address) {
                    throw new Error(`[WebSessionManager] Refusing to store session: address (${address}) does not match idToken custom:walletAddress (${tokenWallet})`);
                }
                if (!tokenWallet) {
                    console.warn("[WebSessionManager] storeSession: idToken has no custom:walletAddress claim — writing without validation");
                }
            }
        }
        catch (err) {
            if (typeof (err === null || err === void 0 ? void 0 : err.message) === "string" && err.message.includes("Refusing to store session")) {
                throw err;
            }
            console.warn("[WebSessionManager] storeSession: failed to decode idToken for validation:", err);
        }
        const config = await getConfig();
        const currentAppId = config.appId;
        localStorage.setItem(this.TAROBASE_SESSION_STORAGE_KEY, JSON.stringify({
            address,
            accessToken,
            idToken,
            refreshToken,
            appId: currentAppId,
        }));
    }
    /* ------------------------------------------------------------------ */
    /* GET                                                                */
    /* ------------------------------------------------------------------ */
    static async getSession() {
        if (typeof window === "undefined")
            return null;
        const session = localStorage.getItem(this.TAROBASE_SESSION_STORAGE_KEY);
        if (!session)
            return null;
        const sessionObj = JSON.parse(session);
        /* ---------- validate app-id ---------- */
        const config = await getConfig();
        if (sessionObj.appId && sessionObj.appId !== config.appId) {
            this.clearSession();
            return null;
        }
        /* ---------- check JWT expiration ---------- */
        try {
            const { accessToken } = sessionObj;
            const { exp } = JSON.parse(this.decodeBase64Url(accessToken.split(".")[1]));
            if (Date.now() > exp * 1000) {
                const { refreshToken } = sessionObj;
                if (!refreshToken)
                    return null;
                const refreshed = await refreshSession(refreshToken);
                if ((refreshed === null || refreshed === void 0 ? void 0 : refreshed.idToken) && (refreshed === null || refreshed === void 0 ? void 0 : refreshed.accessToken)) {
                    await this.updateIdTokenAndAccessToken(refreshed.idToken, refreshed.accessToken);
                    const newSession = localStorage.getItem(this.TAROBASE_SESSION_STORAGE_KEY);
                    if (!newSession)
                        return null;
                    const newObj = JSON.parse(newSession);
                    return { address: newObj.address, session: newObj };
                }
                // Refresh failed — clear stale session to prevent retry loops
                this.clearSession();
                return null;
            }
        }
        catch (err) {
            // Token decode or refresh failed — clear stale session to prevent retry loops
            this.clearSession();
            return null;
        }
        return { address: sessionObj.address, session: sessionObj };
    }
    /* ------------------------------------------------------------------ */
    /* CLEAR                                                              */
    /* ------------------------------------------------------------------ */
    static clearSession() {
        if (typeof window === "undefined")
            return;
        localStorage.removeItem(this.TAROBASE_SESSION_STORAGE_KEY);
    }
    /* ------------------------------------------------------------------ */
    /* IS-AUTH                                                            */
    /* ------------------------------------------------------------------ */
    static isAuthenticated() {
        // NOTE: this remains synchronous to stay backward compatible
        return !!localStorage.getItem(this.TAROBASE_SESSION_STORAGE_KEY);
    }
    /* ------------------------------------------------------------------ */
    /* TOKEN HELPERS                                                      */
    /* ------------------------------------------------------------------ */
    static getIdToken() {
        if (typeof window === "undefined")
            return null;
        const session = localStorage.getItem(this.TAROBASE_SESSION_STORAGE_KEY);
        return session ? JSON.parse(session).idToken : null;
    }
    static getRefreshToken() {
        if (typeof window === "undefined")
            return null;
        const session = localStorage.getItem(this.TAROBASE_SESSION_STORAGE_KEY);
        return session ? JSON.parse(session).refreshToken : null;
    }
    /* ------------------------------------------------------------------ */
    /* UPDATE TOKENS                                                      */
    /* ------------------------------------------------------------------ */
    static async updateIdTokenAndAccessToken(idToken, accessToken) {
        var _a;
        if (typeof window === "undefined")
            return;
        if (typeof window === "undefined")
            return;
        const session = localStorage.getItem(this.TAROBASE_SESSION_STORAGE_KEY);
        if (!session)
            return;
        const sessionObj = JSON.parse(session);
        /* ---------- app-id guard ---------- */
        const config = await getConfig();
        if (sessionObj.appId && sessionObj.appId !== config.appId) {
            this.clearSession();
            return;
        }
        sessionObj.idToken = idToken;
        sessionObj.accessToken = accessToken;
        (_a = sessionObj.appId) !== null && _a !== void 0 ? _a : (sessionObj.appId = config.appId);
        localStorage.setItem(this.TAROBASE_SESSION_STORAGE_KEY, JSON.stringify(sessionObj));
    }
}
WebSessionManager.TAROBASE_SESSION_STORAGE_KEY = "tarobase_session_storage";

var webSessionManager = /*#__PURE__*/Object.freeze({
	__proto__: null,
	WebSessionManager: WebSessionManager
});

var buffer = {};

var base64Js = {};

var hasRequiredBase64Js;

function requireBase64Js () {
	if (hasRequiredBase64Js) return base64Js;
	hasRequiredBase64Js = 1;

	base64Js.byteLength = byteLength;
	base64Js.toByteArray = toByteArray;
	base64Js.fromByteArray = fromByteArray;

	var lookup = [];
	var revLookup = [];
	var Arr = typeof Uint8Array !== 'undefined' ? Uint8Array : Array;

	var code = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
	for (var i = 0, len = code.length; i < len; ++i) {
	  lookup[i] = code[i];
	  revLookup[code.charCodeAt(i)] = i;
	}

	// Support decoding URL-safe base64 strings, as Node.js does.
	// See: https://en.wikipedia.org/wiki/Base64#URL_applications
	revLookup['-'.charCodeAt(0)] = 62;
	revLookup['_'.charCodeAt(0)] = 63;

	function getLens (b64) {
	  var len = b64.length;

	  if (len % 4 > 0) {
	    throw new Error('Invalid string. Length must be a multiple of 4')
	  }

	  // Trim off extra bytes after placeholder bytes are found
	  // See: https://github.com/beatgammit/base64-js/issues/42
	  var validLen = b64.indexOf('=');
	  if (validLen === -1) validLen = len;

	  var placeHoldersLen = validLen === len
	    ? 0
	    : 4 - (validLen % 4);

	  return [validLen, placeHoldersLen]
	}

	// base64 is 4/3 + up to two characters of the original data
	function byteLength (b64) {
	  var lens = getLens(b64);
	  var validLen = lens[0];
	  var placeHoldersLen = lens[1];
	  return ((validLen + placeHoldersLen) * 3 / 4) - placeHoldersLen
	}

	function _byteLength (b64, validLen, placeHoldersLen) {
	  return ((validLen + placeHoldersLen) * 3 / 4) - placeHoldersLen
	}

	function toByteArray (b64) {
	  var tmp;
	  var lens = getLens(b64);
	  var validLen = lens[0];
	  var placeHoldersLen = lens[1];

	  var arr = new Arr(_byteLength(b64, validLen, placeHoldersLen));

	  var curByte = 0;

	  // if there are placeholders, only get up to the last complete 4 chars
	  var len = placeHoldersLen > 0
	    ? validLen - 4
	    : validLen;

	  var i;
	  for (i = 0; i < len; i += 4) {
	    tmp =
	      (revLookup[b64.charCodeAt(i)] << 18) |
	      (revLookup[b64.charCodeAt(i + 1)] << 12) |
	      (revLookup[b64.charCodeAt(i + 2)] << 6) |
	      revLookup[b64.charCodeAt(i + 3)];
	    arr[curByte++] = (tmp >> 16) & 0xFF;
	    arr[curByte++] = (tmp >> 8) & 0xFF;
	    arr[curByte++] = tmp & 0xFF;
	  }

	  if (placeHoldersLen === 2) {
	    tmp =
	      (revLookup[b64.charCodeAt(i)] << 2) |
	      (revLookup[b64.charCodeAt(i + 1)] >> 4);
	    arr[curByte++] = tmp & 0xFF;
	  }

	  if (placeHoldersLen === 1) {
	    tmp =
	      (revLookup[b64.charCodeAt(i)] << 10) |
	      (revLookup[b64.charCodeAt(i + 1)] << 4) |
	      (revLookup[b64.charCodeAt(i + 2)] >> 2);
	    arr[curByte++] = (tmp >> 8) & 0xFF;
	    arr[curByte++] = tmp & 0xFF;
	  }

	  return arr
	}

	function tripletToBase64 (num) {
	  return lookup[num >> 18 & 0x3F] +
	    lookup[num >> 12 & 0x3F] +
	    lookup[num >> 6 & 0x3F] +
	    lookup[num & 0x3F]
	}

	function encodeChunk (uint8, start, end) {
	  var tmp;
	  var output = [];
	  for (var i = start; i < end; i += 3) {
	    tmp =
	      ((uint8[i] << 16) & 0xFF0000) +
	      ((uint8[i + 1] << 8) & 0xFF00) +
	      (uint8[i + 2] & 0xFF);
	    output.push(tripletToBase64(tmp));
	  }
	  return output.join('')
	}

	function fromByteArray (uint8) {
	  var tmp;
	  var len = uint8.length;
	  var extraBytes = len % 3; // if we have 1 byte left, pad 2 bytes
	  var parts = [];
	  var maxChunkLength = 16383; // must be multiple of 3

	  // go through the array every three bytes, we'll deal with trailing stuff later
	  for (var i = 0, len2 = len - extraBytes; i < len2; i += maxChunkLength) {
	    parts.push(encodeChunk(uint8, i, (i + maxChunkLength) > len2 ? len2 : (i + maxChunkLength)));
	  }

	  // pad the end with zeros, but make sure to not forget the extra bytes
	  if (extraBytes === 1) {
	    tmp = uint8[len - 1];
	    parts.push(
	      lookup[tmp >> 2] +
	      lookup[(tmp << 4) & 0x3F] +
	      '=='
	    );
	  } else if (extraBytes === 2) {
	    tmp = (uint8[len - 2] << 8) + uint8[len - 1];
	    parts.push(
	      lookup[tmp >> 10] +
	      lookup[(tmp >> 4) & 0x3F] +
	      lookup[(tmp << 2) & 0x3F] +
	      '='
	    );
	  }

	  return parts.join('')
	}
	return base64Js;
}

var ieee754 = {};

/*! ieee754. BSD-3-Clause License. Feross Aboukhadijeh <https://feross.org/opensource> */

var hasRequiredIeee754;

function requireIeee754 () {
	if (hasRequiredIeee754) return ieee754;
	hasRequiredIeee754 = 1;
	ieee754.read = function (buffer, offset, isLE, mLen, nBytes) {
	  var e, m;
	  var eLen = (nBytes * 8) - mLen - 1;
	  var eMax = (1 << eLen) - 1;
	  var eBias = eMax >> 1;
	  var nBits = -7;
	  var i = isLE ? (nBytes - 1) : 0;
	  var d = isLE ? -1 : 1;
	  var s = buffer[offset + i];

	  i += d;

	  e = s & ((1 << (-nBits)) - 1);
	  s >>= (-nBits);
	  nBits += eLen;
	  for (; nBits > 0; e = (e * 256) + buffer[offset + i], i += d, nBits -= 8) {}

	  m = e & ((1 << (-nBits)) - 1);
	  e >>= (-nBits);
	  nBits += mLen;
	  for (; nBits > 0; m = (m * 256) + buffer[offset + i], i += d, nBits -= 8) {}

	  if (e === 0) {
	    e = 1 - eBias;
	  } else if (e === eMax) {
	    return m ? NaN : ((s ? -1 : 1) * Infinity)
	  } else {
	    m = m + Math.pow(2, mLen);
	    e = e - eBias;
	  }
	  return (s ? -1 : 1) * m * Math.pow(2, e - mLen)
	};

	ieee754.write = function (buffer, value, offset, isLE, mLen, nBytes) {
	  var e, m, c;
	  var eLen = (nBytes * 8) - mLen - 1;
	  var eMax = (1 << eLen) - 1;
	  var eBias = eMax >> 1;
	  var rt = (mLen === 23 ? Math.pow(2, -24) - Math.pow(2, -77) : 0);
	  var i = isLE ? 0 : (nBytes - 1);
	  var d = isLE ? 1 : -1;
	  var s = value < 0 || (value === 0 && 1 / value < 0) ? 1 : 0;

	  value = Math.abs(value);

	  if (isNaN(value) || value === Infinity) {
	    m = isNaN(value) ? 1 : 0;
	    e = eMax;
	  } else {
	    e = Math.floor(Math.log(value) / Math.LN2);
	    if (value * (c = Math.pow(2, -e)) < 1) {
	      e--;
	      c *= 2;
	    }
	    if (e + eBias >= 1) {
	      value += rt / c;
	    } else {
	      value += rt * Math.pow(2, 1 - eBias);
	    }
	    if (value * c >= 2) {
	      e++;
	      c /= 2;
	    }

	    if (e + eBias >= eMax) {
	      m = 0;
	      e = eMax;
	    } else if (e + eBias >= 1) {
	      m = ((value * c) - 1) * Math.pow(2, mLen);
	      e = e + eBias;
	    } else {
	      m = value * Math.pow(2, eBias - 1) * Math.pow(2, mLen);
	      e = 0;
	    }
	  }

	  for (; mLen >= 8; buffer[offset + i] = m & 0xff, i += d, m /= 256, mLen -= 8) {}

	  e = (e << mLen) | m;
	  eLen += mLen;
	  for (; eLen > 0; buffer[offset + i] = e & 0xff, i += d, e /= 256, eLen -= 8) {}

	  buffer[offset + i - d] |= s * 128;
	};
	return ieee754;
}

/*!
 * The buffer module from node.js, for the browser.
 *
 * @author   Feross Aboukhadijeh <https://feross.org>
 * @license  MIT
 */

var hasRequiredBuffer;

function requireBuffer () {
	if (hasRequiredBuffer) return buffer;
	hasRequiredBuffer = 1;
	(function (exports$1) {

		const base64 = requireBase64Js();
		const ieee754 = requireIeee754();
		const customInspectSymbol =
		  (typeof Symbol === 'function' && typeof Symbol['for'] === 'function') // eslint-disable-line dot-notation
		    ? Symbol['for']('nodejs.util.inspect.custom') // eslint-disable-line dot-notation
		    : null;

		exports$1.Buffer = Buffer;
		exports$1.SlowBuffer = SlowBuffer;
		exports$1.INSPECT_MAX_BYTES = 50;

		const K_MAX_LENGTH = 0x7fffffff;
		exports$1.kMaxLength = K_MAX_LENGTH;

		/**
		 * If `Buffer.TYPED_ARRAY_SUPPORT`:
		 *   === true    Use Uint8Array implementation (fastest)
		 *   === false   Print warning and recommend using `buffer` v4.x which has an Object
		 *               implementation (most compatible, even IE6)
		 *
		 * Browsers that support typed arrays are IE 10+, Firefox 4+, Chrome 7+, Safari 5.1+,
		 * Opera 11.6+, iOS 4.2+.
		 *
		 * We report that the browser does not support typed arrays if the are not subclassable
		 * using __proto__. Firefox 4-29 lacks support for adding new properties to `Uint8Array`
		 * (See: https://bugzilla.mozilla.org/show_bug.cgi?id=695438). IE 10 lacks support
		 * for __proto__ and has a buggy typed array implementation.
		 */
		Buffer.TYPED_ARRAY_SUPPORT = typedArraySupport();

		if (!Buffer.TYPED_ARRAY_SUPPORT && typeof console !== 'undefined' &&
		    typeof console.error === 'function') {
		  console.error(
		    'This browser lacks typed array (Uint8Array) support which is required by ' +
		    '`buffer` v5.x. Use `buffer` v4.x if you require old browser support.'
		  );
		}

		function typedArraySupport () {
		  // Can typed array instances can be augmented?
		  try {
		    const arr = new Uint8Array(1);
		    const proto = { foo: function () { return 42 } };
		    Object.setPrototypeOf(proto, Uint8Array.prototype);
		    Object.setPrototypeOf(arr, proto);
		    return arr.foo() === 42
		  } catch (e) {
		    return false
		  }
		}

		Object.defineProperty(Buffer.prototype, 'parent', {
		  enumerable: true,
		  get: function () {
		    if (!Buffer.isBuffer(this)) return undefined
		    return this.buffer
		  }
		});

		Object.defineProperty(Buffer.prototype, 'offset', {
		  enumerable: true,
		  get: function () {
		    if (!Buffer.isBuffer(this)) return undefined
		    return this.byteOffset
		  }
		});

		function createBuffer (length) {
		  if (length > K_MAX_LENGTH) {
		    throw new RangeError('The value "' + length + '" is invalid for option "size"')
		  }
		  // Return an augmented `Uint8Array` instance
		  const buf = new Uint8Array(length);
		  Object.setPrototypeOf(buf, Buffer.prototype);
		  return buf
		}

		/**
		 * The Buffer constructor returns instances of `Uint8Array` that have their
		 * prototype changed to `Buffer.prototype`. Furthermore, `Buffer` is a subclass of
		 * `Uint8Array`, so the returned instances will have all the node `Buffer` methods
		 * and the `Uint8Array` methods. Square bracket notation works as expected -- it
		 * returns a single octet.
		 *
		 * The `Uint8Array` prototype remains unmodified.
		 */

		function Buffer (arg, encodingOrOffset, length) {
		  // Common case.
		  if (typeof arg === 'number') {
		    if (typeof encodingOrOffset === 'string') {
		      throw new TypeError(
		        'The "string" argument must be of type string. Received type number'
		      )
		    }
		    return allocUnsafe(arg)
		  }
		  return from(arg, encodingOrOffset, length)
		}

		Buffer.poolSize = 8192; // not used by this implementation

		function from (value, encodingOrOffset, length) {
		  if (typeof value === 'string') {
		    return fromString(value, encodingOrOffset)
		  }

		  if (ArrayBuffer.isView(value)) {
		    return fromArrayView(value)
		  }

		  if (value == null) {
		    throw new TypeError(
		      'The first argument must be one of type string, Buffer, ArrayBuffer, Array, ' +
		      'or Array-like Object. Received type ' + (typeof value)
		    )
		  }

		  if (isInstance(value, ArrayBuffer) ||
		      (value && isInstance(value.buffer, ArrayBuffer))) {
		    return fromArrayBuffer(value, encodingOrOffset, length)
		  }

		  if (typeof SharedArrayBuffer !== 'undefined' &&
		      (isInstance(value, SharedArrayBuffer) ||
		      (value && isInstance(value.buffer, SharedArrayBuffer)))) {
		    return fromArrayBuffer(value, encodingOrOffset, length)
		  }

		  if (typeof value === 'number') {
		    throw new TypeError(
		      'The "value" argument must not be of type number. Received type number'
		    )
		  }

		  const valueOf = value.valueOf && value.valueOf();
		  if (valueOf != null && valueOf !== value) {
		    return Buffer.from(valueOf, encodingOrOffset, length)
		  }

		  const b = fromObject(value);
		  if (b) return b

		  if (typeof Symbol !== 'undefined' && Symbol.toPrimitive != null &&
		      typeof value[Symbol.toPrimitive] === 'function') {
		    return Buffer.from(value[Symbol.toPrimitive]('string'), encodingOrOffset, length)
		  }

		  throw new TypeError(
		    'The first argument must be one of type string, Buffer, ArrayBuffer, Array, ' +
		    'or Array-like Object. Received type ' + (typeof value)
		  )
		}

		/**
		 * Functionally equivalent to Buffer(arg, encoding) but throws a TypeError
		 * if value is a number.
		 * Buffer.from(str[, encoding])
		 * Buffer.from(array)
		 * Buffer.from(buffer)
		 * Buffer.from(arrayBuffer[, byteOffset[, length]])
		 **/
		Buffer.from = function (value, encodingOrOffset, length) {
		  return from(value, encodingOrOffset, length)
		};

		// Note: Change prototype *after* Buffer.from is defined to workaround Chrome bug:
		// https://github.com/feross/buffer/pull/148
		Object.setPrototypeOf(Buffer.prototype, Uint8Array.prototype);
		Object.setPrototypeOf(Buffer, Uint8Array);

		function assertSize (size) {
		  if (typeof size !== 'number') {
		    throw new TypeError('"size" argument must be of type number')
		  } else if (size < 0) {
		    throw new RangeError('The value "' + size + '" is invalid for option "size"')
		  }
		}

		function alloc (size, fill, encoding) {
		  assertSize(size);
		  if (size <= 0) {
		    return createBuffer(size)
		  }
		  if (fill !== undefined) {
		    // Only pay attention to encoding if it's a string. This
		    // prevents accidentally sending in a number that would
		    // be interpreted as a start offset.
		    return typeof encoding === 'string'
		      ? createBuffer(size).fill(fill, encoding)
		      : createBuffer(size).fill(fill)
		  }
		  return createBuffer(size)
		}

		/**
		 * Creates a new filled Buffer instance.
		 * alloc(size[, fill[, encoding]])
		 **/
		Buffer.alloc = function (size, fill, encoding) {
		  return alloc(size, fill, encoding)
		};

		function allocUnsafe (size) {
		  assertSize(size);
		  return createBuffer(size < 0 ? 0 : checked(size) | 0)
		}

		/**
		 * Equivalent to Buffer(num), by default creates a non-zero-filled Buffer instance.
		 * */
		Buffer.allocUnsafe = function (size) {
		  return allocUnsafe(size)
		};
		/**
		 * Equivalent to SlowBuffer(num), by default creates a non-zero-filled Buffer instance.
		 */
		Buffer.allocUnsafeSlow = function (size) {
		  return allocUnsafe(size)
		};

		function fromString (string, encoding) {
		  if (typeof encoding !== 'string' || encoding === '') {
		    encoding = 'utf8';
		  }

		  if (!Buffer.isEncoding(encoding)) {
		    throw new TypeError('Unknown encoding: ' + encoding)
		  }

		  const length = byteLength(string, encoding) | 0;
		  let buf = createBuffer(length);

		  const actual = buf.write(string, encoding);

		  if (actual !== length) {
		    // Writing a hex string, for example, that contains invalid characters will
		    // cause everything after the first invalid character to be ignored. (e.g.
		    // 'abxxcd' will be treated as 'ab')
		    buf = buf.slice(0, actual);
		  }

		  return buf
		}

		function fromArrayLike (array) {
		  const length = array.length < 0 ? 0 : checked(array.length) | 0;
		  const buf = createBuffer(length);
		  for (let i = 0; i < length; i += 1) {
		    buf[i] = array[i] & 255;
		  }
		  return buf
		}

		function fromArrayView (arrayView) {
		  if (isInstance(arrayView, Uint8Array)) {
		    const copy = new Uint8Array(arrayView);
		    return fromArrayBuffer(copy.buffer, copy.byteOffset, copy.byteLength)
		  }
		  return fromArrayLike(arrayView)
		}

		function fromArrayBuffer (array, byteOffset, length) {
		  if (byteOffset < 0 || array.byteLength < byteOffset) {
		    throw new RangeError('"offset" is outside of buffer bounds')
		  }

		  if (array.byteLength < byteOffset + (length || 0)) {
		    throw new RangeError('"length" is outside of buffer bounds')
		  }

		  let buf;
		  if (byteOffset === undefined && length === undefined) {
		    buf = new Uint8Array(array);
		  } else if (length === undefined) {
		    buf = new Uint8Array(array, byteOffset);
		  } else {
		    buf = new Uint8Array(array, byteOffset, length);
		  }

		  // Return an augmented `Uint8Array` instance
		  Object.setPrototypeOf(buf, Buffer.prototype);

		  return buf
		}

		function fromObject (obj) {
		  if (Buffer.isBuffer(obj)) {
		    const len = checked(obj.length) | 0;
		    const buf = createBuffer(len);

		    if (buf.length === 0) {
		      return buf
		    }

		    obj.copy(buf, 0, 0, len);
		    return buf
		  }

		  if (obj.length !== undefined) {
		    if (typeof obj.length !== 'number' || numberIsNaN(obj.length)) {
		      return createBuffer(0)
		    }
		    return fromArrayLike(obj)
		  }

		  if (obj.type === 'Buffer' && Array.isArray(obj.data)) {
		    return fromArrayLike(obj.data)
		  }
		}

		function checked (length) {
		  // Note: cannot use `length < K_MAX_LENGTH` here because that fails when
		  // length is NaN (which is otherwise coerced to zero.)
		  if (length >= K_MAX_LENGTH) {
		    throw new RangeError('Attempt to allocate Buffer larger than maximum ' +
		                         'size: 0x' + K_MAX_LENGTH.toString(16) + ' bytes')
		  }
		  return length | 0
		}

		function SlowBuffer (length) {
		  if (+length != length) { // eslint-disable-line eqeqeq
		    length = 0;
		  }
		  return Buffer.alloc(+length)
		}

		Buffer.isBuffer = function isBuffer (b) {
		  return b != null && b._isBuffer === true &&
		    b !== Buffer.prototype // so Buffer.isBuffer(Buffer.prototype) will be false
		};

		Buffer.compare = function compare (a, b) {
		  if (isInstance(a, Uint8Array)) a = Buffer.from(a, a.offset, a.byteLength);
		  if (isInstance(b, Uint8Array)) b = Buffer.from(b, b.offset, b.byteLength);
		  if (!Buffer.isBuffer(a) || !Buffer.isBuffer(b)) {
		    throw new TypeError(
		      'The "buf1", "buf2" arguments must be one of type Buffer or Uint8Array'
		    )
		  }

		  if (a === b) return 0

		  let x = a.length;
		  let y = b.length;

		  for (let i = 0, len = Math.min(x, y); i < len; ++i) {
		    if (a[i] !== b[i]) {
		      x = a[i];
		      y = b[i];
		      break
		    }
		  }

		  if (x < y) return -1
		  if (y < x) return 1
		  return 0
		};

		Buffer.isEncoding = function isEncoding (encoding) {
		  switch (String(encoding).toLowerCase()) {
		    case 'hex':
		    case 'utf8':
		    case 'utf-8':
		    case 'ascii':
		    case 'latin1':
		    case 'binary':
		    case 'base64':
		    case 'ucs2':
		    case 'ucs-2':
		    case 'utf16le':
		    case 'utf-16le':
		      return true
		    default:
		      return false
		  }
		};

		Buffer.concat = function concat (list, length) {
		  if (!Array.isArray(list)) {
		    throw new TypeError('"list" argument must be an Array of Buffers')
		  }

		  if (list.length === 0) {
		    return Buffer.alloc(0)
		  }

		  let i;
		  if (length === undefined) {
		    length = 0;
		    for (i = 0; i < list.length; ++i) {
		      length += list[i].length;
		    }
		  }

		  const buffer = Buffer.allocUnsafe(length);
		  let pos = 0;
		  for (i = 0; i < list.length; ++i) {
		    let buf = list[i];
		    if (isInstance(buf, Uint8Array)) {
		      if (pos + buf.length > buffer.length) {
		        if (!Buffer.isBuffer(buf)) buf = Buffer.from(buf);
		        buf.copy(buffer, pos);
		      } else {
		        Uint8Array.prototype.set.call(
		          buffer,
		          buf,
		          pos
		        );
		      }
		    } else if (!Buffer.isBuffer(buf)) {
		      throw new TypeError('"list" argument must be an Array of Buffers')
		    } else {
		      buf.copy(buffer, pos);
		    }
		    pos += buf.length;
		  }
		  return buffer
		};

		function byteLength (string, encoding) {
		  if (Buffer.isBuffer(string)) {
		    return string.length
		  }
		  if (ArrayBuffer.isView(string) || isInstance(string, ArrayBuffer)) {
		    return string.byteLength
		  }
		  if (typeof string !== 'string') {
		    throw new TypeError(
		      'The "string" argument must be one of type string, Buffer, or ArrayBuffer. ' +
		      'Received type ' + typeof string
		    )
		  }

		  const len = string.length;
		  const mustMatch = (arguments.length > 2 && arguments[2] === true);
		  if (!mustMatch && len === 0) return 0

		  // Use a for loop to avoid recursion
		  let loweredCase = false;
		  for (;;) {
		    switch (encoding) {
		      case 'ascii':
		      case 'latin1':
		      case 'binary':
		        return len
		      case 'utf8':
		      case 'utf-8':
		        return utf8ToBytes(string).length
		      case 'ucs2':
		      case 'ucs-2':
		      case 'utf16le':
		      case 'utf-16le':
		        return len * 2
		      case 'hex':
		        return len >>> 1
		      case 'base64':
		        return base64ToBytes(string).length
		      default:
		        if (loweredCase) {
		          return mustMatch ? -1 : utf8ToBytes(string).length // assume utf8
		        }
		        encoding = ('' + encoding).toLowerCase();
		        loweredCase = true;
		    }
		  }
		}
		Buffer.byteLength = byteLength;

		function slowToString (encoding, start, end) {
		  let loweredCase = false;

		  // No need to verify that "this.length <= MAX_UINT32" since it's a read-only
		  // property of a typed array.

		  // This behaves neither like String nor Uint8Array in that we set start/end
		  // to their upper/lower bounds if the value passed is out of range.
		  // undefined is handled specially as per ECMA-262 6th Edition,
		  // Section 13.3.3.7 Runtime Semantics: KeyedBindingInitialization.
		  if (start === undefined || start < 0) {
		    start = 0;
		  }
		  // Return early if start > this.length. Done here to prevent potential uint32
		  // coercion fail below.
		  if (start > this.length) {
		    return ''
		  }

		  if (end === undefined || end > this.length) {
		    end = this.length;
		  }

		  if (end <= 0) {
		    return ''
		  }

		  // Force coercion to uint32. This will also coerce falsey/NaN values to 0.
		  end >>>= 0;
		  start >>>= 0;

		  if (end <= start) {
		    return ''
		  }

		  if (!encoding) encoding = 'utf8';

		  while (true) {
		    switch (encoding) {
		      case 'hex':
		        return hexSlice(this, start, end)

		      case 'utf8':
		      case 'utf-8':
		        return utf8Slice(this, start, end)

		      case 'ascii':
		        return asciiSlice(this, start, end)

		      case 'latin1':
		      case 'binary':
		        return latin1Slice(this, start, end)

		      case 'base64':
		        return base64Slice(this, start, end)

		      case 'ucs2':
		      case 'ucs-2':
		      case 'utf16le':
		      case 'utf-16le':
		        return utf16leSlice(this, start, end)

		      default:
		        if (loweredCase) throw new TypeError('Unknown encoding: ' + encoding)
		        encoding = (encoding + '').toLowerCase();
		        loweredCase = true;
		    }
		  }
		}

		// This property is used by `Buffer.isBuffer` (and the `is-buffer` npm package)
		// to detect a Buffer instance. It's not possible to use `instanceof Buffer`
		// reliably in a browserify context because there could be multiple different
		// copies of the 'buffer' package in use. This method works even for Buffer
		// instances that were created from another copy of the `buffer` package.
		// See: https://github.com/feross/buffer/issues/154
		Buffer.prototype._isBuffer = true;

		function swap (b, n, m) {
		  const i = b[n];
		  b[n] = b[m];
		  b[m] = i;
		}

		Buffer.prototype.swap16 = function swap16 () {
		  const len = this.length;
		  if (len % 2 !== 0) {
		    throw new RangeError('Buffer size must be a multiple of 16-bits')
		  }
		  for (let i = 0; i < len; i += 2) {
		    swap(this, i, i + 1);
		  }
		  return this
		};

		Buffer.prototype.swap32 = function swap32 () {
		  const len = this.length;
		  if (len % 4 !== 0) {
		    throw new RangeError('Buffer size must be a multiple of 32-bits')
		  }
		  for (let i = 0; i < len; i += 4) {
		    swap(this, i, i + 3);
		    swap(this, i + 1, i + 2);
		  }
		  return this
		};

		Buffer.prototype.swap64 = function swap64 () {
		  const len = this.length;
		  if (len % 8 !== 0) {
		    throw new RangeError('Buffer size must be a multiple of 64-bits')
		  }
		  for (let i = 0; i < len; i += 8) {
		    swap(this, i, i + 7);
		    swap(this, i + 1, i + 6);
		    swap(this, i + 2, i + 5);
		    swap(this, i + 3, i + 4);
		  }
		  return this
		};

		Buffer.prototype.toString = function toString () {
		  const length = this.length;
		  if (length === 0) return ''
		  if (arguments.length === 0) return utf8Slice(this, 0, length)
		  return slowToString.apply(this, arguments)
		};

		Buffer.prototype.toLocaleString = Buffer.prototype.toString;

		Buffer.prototype.equals = function equals (b) {
		  if (!Buffer.isBuffer(b)) throw new TypeError('Argument must be a Buffer')
		  if (this === b) return true
		  return Buffer.compare(this, b) === 0
		};

		Buffer.prototype.inspect = function inspect () {
		  let str = '';
		  const max = exports$1.INSPECT_MAX_BYTES;
		  str = this.toString('hex', 0, max).replace(/(.{2})/g, '$1 ').trim();
		  if (this.length > max) str += ' ... ';
		  return '<Buffer ' + str + '>'
		};
		if (customInspectSymbol) {
		  Buffer.prototype[customInspectSymbol] = Buffer.prototype.inspect;
		}

		Buffer.prototype.compare = function compare (target, start, end, thisStart, thisEnd) {
		  if (isInstance(target, Uint8Array)) {
		    target = Buffer.from(target, target.offset, target.byteLength);
		  }
		  if (!Buffer.isBuffer(target)) {
		    throw new TypeError(
		      'The "target" argument must be one of type Buffer or Uint8Array. ' +
		      'Received type ' + (typeof target)
		    )
		  }

		  if (start === undefined) {
		    start = 0;
		  }
		  if (end === undefined) {
		    end = target ? target.length : 0;
		  }
		  if (thisStart === undefined) {
		    thisStart = 0;
		  }
		  if (thisEnd === undefined) {
		    thisEnd = this.length;
		  }

		  if (start < 0 || end > target.length || thisStart < 0 || thisEnd > this.length) {
		    throw new RangeError('out of range index')
		  }

		  if (thisStart >= thisEnd && start >= end) {
		    return 0
		  }
		  if (thisStart >= thisEnd) {
		    return -1
		  }
		  if (start >= end) {
		    return 1
		  }

		  start >>>= 0;
		  end >>>= 0;
		  thisStart >>>= 0;
		  thisEnd >>>= 0;

		  if (this === target) return 0

		  let x = thisEnd - thisStart;
		  let y = end - start;
		  const len = Math.min(x, y);

		  const thisCopy = this.slice(thisStart, thisEnd);
		  const targetCopy = target.slice(start, end);

		  for (let i = 0; i < len; ++i) {
		    if (thisCopy[i] !== targetCopy[i]) {
		      x = thisCopy[i];
		      y = targetCopy[i];
		      break
		    }
		  }

		  if (x < y) return -1
		  if (y < x) return 1
		  return 0
		};

		// Finds either the first index of `val` in `buffer` at offset >= `byteOffset`,
		// OR the last index of `val` in `buffer` at offset <= `byteOffset`.
		//
		// Arguments:
		// - buffer - a Buffer to search
		// - val - a string, Buffer, or number
		// - byteOffset - an index into `buffer`; will be clamped to an int32
		// - encoding - an optional encoding, relevant is val is a string
		// - dir - true for indexOf, false for lastIndexOf
		function bidirectionalIndexOf (buffer, val, byteOffset, encoding, dir) {
		  // Empty buffer means no match
		  if (buffer.length === 0) return -1

		  // Normalize byteOffset
		  if (typeof byteOffset === 'string') {
		    encoding = byteOffset;
		    byteOffset = 0;
		  } else if (byteOffset > 0x7fffffff) {
		    byteOffset = 0x7fffffff;
		  } else if (byteOffset < -2147483648) {
		    byteOffset = -2147483648;
		  }
		  byteOffset = +byteOffset; // Coerce to Number.
		  if (numberIsNaN(byteOffset)) {
		    // byteOffset: it it's undefined, null, NaN, "foo", etc, search whole buffer
		    byteOffset = dir ? 0 : (buffer.length - 1);
		  }

		  // Normalize byteOffset: negative offsets start from the end of the buffer
		  if (byteOffset < 0) byteOffset = buffer.length + byteOffset;
		  if (byteOffset >= buffer.length) {
		    if (dir) return -1
		    else byteOffset = buffer.length - 1;
		  } else if (byteOffset < 0) {
		    if (dir) byteOffset = 0;
		    else return -1
		  }

		  // Normalize val
		  if (typeof val === 'string') {
		    val = Buffer.from(val, encoding);
		  }

		  // Finally, search either indexOf (if dir is true) or lastIndexOf
		  if (Buffer.isBuffer(val)) {
		    // Special case: looking for empty string/buffer always fails
		    if (val.length === 0) {
		      return -1
		    }
		    return arrayIndexOf(buffer, val, byteOffset, encoding, dir)
		  } else if (typeof val === 'number') {
		    val = val & 0xFF; // Search for a byte value [0-255]
		    if (typeof Uint8Array.prototype.indexOf === 'function') {
		      if (dir) {
		        return Uint8Array.prototype.indexOf.call(buffer, val, byteOffset)
		      } else {
		        return Uint8Array.prototype.lastIndexOf.call(buffer, val, byteOffset)
		      }
		    }
		    return arrayIndexOf(buffer, [val], byteOffset, encoding, dir)
		  }

		  throw new TypeError('val must be string, number or Buffer')
		}

		function arrayIndexOf (arr, val, byteOffset, encoding, dir) {
		  let indexSize = 1;
		  let arrLength = arr.length;
		  let valLength = val.length;

		  if (encoding !== undefined) {
		    encoding = String(encoding).toLowerCase();
		    if (encoding === 'ucs2' || encoding === 'ucs-2' ||
		        encoding === 'utf16le' || encoding === 'utf-16le') {
		      if (arr.length < 2 || val.length < 2) {
		        return -1
		      }
		      indexSize = 2;
		      arrLength /= 2;
		      valLength /= 2;
		      byteOffset /= 2;
		    }
		  }

		  function read (buf, i) {
		    if (indexSize === 1) {
		      return buf[i]
		    } else {
		      return buf.readUInt16BE(i * indexSize)
		    }
		  }

		  let i;
		  if (dir) {
		    let foundIndex = -1;
		    for (i = byteOffset; i < arrLength; i++) {
		      if (read(arr, i) === read(val, foundIndex === -1 ? 0 : i - foundIndex)) {
		        if (foundIndex === -1) foundIndex = i;
		        if (i - foundIndex + 1 === valLength) return foundIndex * indexSize
		      } else {
		        if (foundIndex !== -1) i -= i - foundIndex;
		        foundIndex = -1;
		      }
		    }
		  } else {
		    if (byteOffset + valLength > arrLength) byteOffset = arrLength - valLength;
		    for (i = byteOffset; i >= 0; i--) {
		      let found = true;
		      for (let j = 0; j < valLength; j++) {
		        if (read(arr, i + j) !== read(val, j)) {
		          found = false;
		          break
		        }
		      }
		      if (found) return i
		    }
		  }

		  return -1
		}

		Buffer.prototype.includes = function includes (val, byteOffset, encoding) {
		  return this.indexOf(val, byteOffset, encoding) !== -1
		};

		Buffer.prototype.indexOf = function indexOf (val, byteOffset, encoding) {
		  return bidirectionalIndexOf(this, val, byteOffset, encoding, true)
		};

		Buffer.prototype.lastIndexOf = function lastIndexOf (val, byteOffset, encoding) {
		  return bidirectionalIndexOf(this, val, byteOffset, encoding, false)
		};

		function hexWrite (buf, string, offset, length) {
		  offset = Number(offset) || 0;
		  const remaining = buf.length - offset;
		  if (!length) {
		    length = remaining;
		  } else {
		    length = Number(length);
		    if (length > remaining) {
		      length = remaining;
		    }
		  }

		  const strLen = string.length;

		  if (length > strLen / 2) {
		    length = strLen / 2;
		  }
		  let i;
		  for (i = 0; i < length; ++i) {
		    const parsed = parseInt(string.substr(i * 2, 2), 16);
		    if (numberIsNaN(parsed)) return i
		    buf[offset + i] = parsed;
		  }
		  return i
		}

		function utf8Write (buf, string, offset, length) {
		  return blitBuffer(utf8ToBytes(string, buf.length - offset), buf, offset, length)
		}

		function asciiWrite (buf, string, offset, length) {
		  return blitBuffer(asciiToBytes(string), buf, offset, length)
		}

		function base64Write (buf, string, offset, length) {
		  return blitBuffer(base64ToBytes(string), buf, offset, length)
		}

		function ucs2Write (buf, string, offset, length) {
		  return blitBuffer(utf16leToBytes(string, buf.length - offset), buf, offset, length)
		}

		Buffer.prototype.write = function write (string, offset, length, encoding) {
		  // Buffer#write(string)
		  if (offset === undefined) {
		    encoding = 'utf8';
		    length = this.length;
		    offset = 0;
		  // Buffer#write(string, encoding)
		  } else if (length === undefined && typeof offset === 'string') {
		    encoding = offset;
		    length = this.length;
		    offset = 0;
		  // Buffer#write(string, offset[, length][, encoding])
		  } else if (isFinite(offset)) {
		    offset = offset >>> 0;
		    if (isFinite(length)) {
		      length = length >>> 0;
		      if (encoding === undefined) encoding = 'utf8';
		    } else {
		      encoding = length;
		      length = undefined;
		    }
		  } else {
		    throw new Error(
		      'Buffer.write(string, encoding, offset[, length]) is no longer supported'
		    )
		  }

		  const remaining = this.length - offset;
		  if (length === undefined || length > remaining) length = remaining;

		  if ((string.length > 0 && (length < 0 || offset < 0)) || offset > this.length) {
		    throw new RangeError('Attempt to write outside buffer bounds')
		  }

		  if (!encoding) encoding = 'utf8';

		  let loweredCase = false;
		  for (;;) {
		    switch (encoding) {
		      case 'hex':
		        return hexWrite(this, string, offset, length)

		      case 'utf8':
		      case 'utf-8':
		        return utf8Write(this, string, offset, length)

		      case 'ascii':
		      case 'latin1':
		      case 'binary':
		        return asciiWrite(this, string, offset, length)

		      case 'base64':
		        // Warning: maxLength not taken into account in base64Write
		        return base64Write(this, string, offset, length)

		      case 'ucs2':
		      case 'ucs-2':
		      case 'utf16le':
		      case 'utf-16le':
		        return ucs2Write(this, string, offset, length)

		      default:
		        if (loweredCase) throw new TypeError('Unknown encoding: ' + encoding)
		        encoding = ('' + encoding).toLowerCase();
		        loweredCase = true;
		    }
		  }
		};

		Buffer.prototype.toJSON = function toJSON () {
		  return {
		    type: 'Buffer',
		    data: Array.prototype.slice.call(this._arr || this, 0)
		  }
		};

		function base64Slice (buf, start, end) {
		  if (start === 0 && end === buf.length) {
		    return base64.fromByteArray(buf)
		  } else {
		    return base64.fromByteArray(buf.slice(start, end))
		  }
		}

		function utf8Slice (buf, start, end) {
		  end = Math.min(buf.length, end);
		  const res = [];

		  let i = start;
		  while (i < end) {
		    const firstByte = buf[i];
		    let codePoint = null;
		    let bytesPerSequence = (firstByte > 0xEF)
		      ? 4
		      : (firstByte > 0xDF)
		          ? 3
		          : (firstByte > 0xBF)
		              ? 2
		              : 1;

		    if (i + bytesPerSequence <= end) {
		      let secondByte, thirdByte, fourthByte, tempCodePoint;

		      switch (bytesPerSequence) {
		        case 1:
		          if (firstByte < 0x80) {
		            codePoint = firstByte;
		          }
		          break
		        case 2:
		          secondByte = buf[i + 1];
		          if ((secondByte & 0xC0) === 0x80) {
		            tempCodePoint = (firstByte & 0x1F) << 0x6 | (secondByte & 0x3F);
		            if (tempCodePoint > 0x7F) {
		              codePoint = tempCodePoint;
		            }
		          }
		          break
		        case 3:
		          secondByte = buf[i + 1];
		          thirdByte = buf[i + 2];
		          if ((secondByte & 0xC0) === 0x80 && (thirdByte & 0xC0) === 0x80) {
		            tempCodePoint = (firstByte & 0xF) << 0xC | (secondByte & 0x3F) << 0x6 | (thirdByte & 0x3F);
		            if (tempCodePoint > 0x7FF && (tempCodePoint < 0xD800 || tempCodePoint > 0xDFFF)) {
		              codePoint = tempCodePoint;
		            }
		          }
		          break
		        case 4:
		          secondByte = buf[i + 1];
		          thirdByte = buf[i + 2];
		          fourthByte = buf[i + 3];
		          if ((secondByte & 0xC0) === 0x80 && (thirdByte & 0xC0) === 0x80 && (fourthByte & 0xC0) === 0x80) {
		            tempCodePoint = (firstByte & 0xF) << 0x12 | (secondByte & 0x3F) << 0xC | (thirdByte & 0x3F) << 0x6 | (fourthByte & 0x3F);
		            if (tempCodePoint > 0xFFFF && tempCodePoint < 0x110000) {
		              codePoint = tempCodePoint;
		            }
		          }
		      }
		    }

		    if (codePoint === null) {
		      // we did not generate a valid codePoint so insert a
		      // replacement char (U+FFFD) and advance only 1 byte
		      codePoint = 0xFFFD;
		      bytesPerSequence = 1;
		    } else if (codePoint > 0xFFFF) {
		      // encode to utf16 (surrogate pair dance)
		      codePoint -= 0x10000;
		      res.push(codePoint >>> 10 & 0x3FF | 0xD800);
		      codePoint = 0xDC00 | codePoint & 0x3FF;
		    }

		    res.push(codePoint);
		    i += bytesPerSequence;
		  }

		  return decodeCodePointsArray(res)
		}

		// Based on http://stackoverflow.com/a/22747272/680742, the browser with
		// the lowest limit is Chrome, with 0x10000 args.
		// We go 1 magnitude less, for safety
		const MAX_ARGUMENTS_LENGTH = 0x1000;

		function decodeCodePointsArray (codePoints) {
		  const len = codePoints.length;
		  if (len <= MAX_ARGUMENTS_LENGTH) {
		    return String.fromCharCode.apply(String, codePoints) // avoid extra slice()
		  }

		  // Decode in chunks to avoid "call stack size exceeded".
		  let res = '';
		  let i = 0;
		  while (i < len) {
		    res += String.fromCharCode.apply(
		      String,
		      codePoints.slice(i, i += MAX_ARGUMENTS_LENGTH)
		    );
		  }
		  return res
		}

		function asciiSlice (buf, start, end) {
		  let ret = '';
		  end = Math.min(buf.length, end);

		  for (let i = start; i < end; ++i) {
		    ret += String.fromCharCode(buf[i] & 0x7F);
		  }
		  return ret
		}

		function latin1Slice (buf, start, end) {
		  let ret = '';
		  end = Math.min(buf.length, end);

		  for (let i = start; i < end; ++i) {
		    ret += String.fromCharCode(buf[i]);
		  }
		  return ret
		}

		function hexSlice (buf, start, end) {
		  const len = buf.length;

		  if (!start || start < 0) start = 0;
		  if (!end || end < 0 || end > len) end = len;

		  let out = '';
		  for (let i = start; i < end; ++i) {
		    out += hexSliceLookupTable[buf[i]];
		  }
		  return out
		}

		function utf16leSlice (buf, start, end) {
		  const bytes = buf.slice(start, end);
		  let res = '';
		  // If bytes.length is odd, the last 8 bits must be ignored (same as node.js)
		  for (let i = 0; i < bytes.length - 1; i += 2) {
		    res += String.fromCharCode(bytes[i] + (bytes[i + 1] * 256));
		  }
		  return res
		}

		Buffer.prototype.slice = function slice (start, end) {
		  const len = this.length;
		  start = ~~start;
		  end = end === undefined ? len : ~~end;

		  if (start < 0) {
		    start += len;
		    if (start < 0) start = 0;
		  } else if (start > len) {
		    start = len;
		  }

		  if (end < 0) {
		    end += len;
		    if (end < 0) end = 0;
		  } else if (end > len) {
		    end = len;
		  }

		  if (end < start) end = start;

		  const newBuf = this.subarray(start, end);
		  // Return an augmented `Uint8Array` instance
		  Object.setPrototypeOf(newBuf, Buffer.prototype);

		  return newBuf
		};

		/*
		 * Need to make sure that buffer isn't trying to write out of bounds.
		 */
		function checkOffset (offset, ext, length) {
		  if ((offset % 1) !== 0 || offset < 0) throw new RangeError('offset is not uint')
		  if (offset + ext > length) throw new RangeError('Trying to access beyond buffer length')
		}

		Buffer.prototype.readUintLE =
		Buffer.prototype.readUIntLE = function readUIntLE (offset, byteLength, noAssert) {
		  offset = offset >>> 0;
		  byteLength = byteLength >>> 0;
		  if (!noAssert) checkOffset(offset, byteLength, this.length);

		  let val = this[offset];
		  let mul = 1;
		  let i = 0;
		  while (++i < byteLength && (mul *= 0x100)) {
		    val += this[offset + i] * mul;
		  }

		  return val
		};

		Buffer.prototype.readUintBE =
		Buffer.prototype.readUIntBE = function readUIntBE (offset, byteLength, noAssert) {
		  offset = offset >>> 0;
		  byteLength = byteLength >>> 0;
		  if (!noAssert) {
		    checkOffset(offset, byteLength, this.length);
		  }

		  let val = this[offset + --byteLength];
		  let mul = 1;
		  while (byteLength > 0 && (mul *= 0x100)) {
		    val += this[offset + --byteLength] * mul;
		  }

		  return val
		};

		Buffer.prototype.readUint8 =
		Buffer.prototype.readUInt8 = function readUInt8 (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 1, this.length);
		  return this[offset]
		};

		Buffer.prototype.readUint16LE =
		Buffer.prototype.readUInt16LE = function readUInt16LE (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 2, this.length);
		  return this[offset] | (this[offset + 1] << 8)
		};

		Buffer.prototype.readUint16BE =
		Buffer.prototype.readUInt16BE = function readUInt16BE (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 2, this.length);
		  return (this[offset] << 8) | this[offset + 1]
		};

		Buffer.prototype.readUint32LE =
		Buffer.prototype.readUInt32LE = function readUInt32LE (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 4, this.length);

		  return ((this[offset]) |
		      (this[offset + 1] << 8) |
		      (this[offset + 2] << 16)) +
		      (this[offset + 3] * 0x1000000)
		};

		Buffer.prototype.readUint32BE =
		Buffer.prototype.readUInt32BE = function readUInt32BE (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 4, this.length);

		  return (this[offset] * 0x1000000) +
		    ((this[offset + 1] << 16) |
		    (this[offset + 2] << 8) |
		    this[offset + 3])
		};

		Buffer.prototype.readBigUInt64LE = defineBigIntMethod(function readBigUInt64LE (offset) {
		  offset = offset >>> 0;
		  validateNumber(offset, 'offset');
		  const first = this[offset];
		  const last = this[offset + 7];
		  if (first === undefined || last === undefined) {
		    boundsError(offset, this.length - 8);
		  }

		  const lo = first +
		    this[++offset] * 2 ** 8 +
		    this[++offset] * 2 ** 16 +
		    this[++offset] * 2 ** 24;

		  const hi = this[++offset] +
		    this[++offset] * 2 ** 8 +
		    this[++offset] * 2 ** 16 +
		    last * 2 ** 24;

		  return BigInt(lo) + (BigInt(hi) << BigInt(32))
		});

		Buffer.prototype.readBigUInt64BE = defineBigIntMethod(function readBigUInt64BE (offset) {
		  offset = offset >>> 0;
		  validateNumber(offset, 'offset');
		  const first = this[offset];
		  const last = this[offset + 7];
		  if (first === undefined || last === undefined) {
		    boundsError(offset, this.length - 8);
		  }

		  const hi = first * 2 ** 24 +
		    this[++offset] * 2 ** 16 +
		    this[++offset] * 2 ** 8 +
		    this[++offset];

		  const lo = this[++offset] * 2 ** 24 +
		    this[++offset] * 2 ** 16 +
		    this[++offset] * 2 ** 8 +
		    last;

		  return (BigInt(hi) << BigInt(32)) + BigInt(lo)
		});

		Buffer.prototype.readIntLE = function readIntLE (offset, byteLength, noAssert) {
		  offset = offset >>> 0;
		  byteLength = byteLength >>> 0;
		  if (!noAssert) checkOffset(offset, byteLength, this.length);

		  let val = this[offset];
		  let mul = 1;
		  let i = 0;
		  while (++i < byteLength && (mul *= 0x100)) {
		    val += this[offset + i] * mul;
		  }
		  mul *= 0x80;

		  if (val >= mul) val -= Math.pow(2, 8 * byteLength);

		  return val
		};

		Buffer.prototype.readIntBE = function readIntBE (offset, byteLength, noAssert) {
		  offset = offset >>> 0;
		  byteLength = byteLength >>> 0;
		  if (!noAssert) checkOffset(offset, byteLength, this.length);

		  let i = byteLength;
		  let mul = 1;
		  let val = this[offset + --i];
		  while (i > 0 && (mul *= 0x100)) {
		    val += this[offset + --i] * mul;
		  }
		  mul *= 0x80;

		  if (val >= mul) val -= Math.pow(2, 8 * byteLength);

		  return val
		};

		Buffer.prototype.readInt8 = function readInt8 (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 1, this.length);
		  if (!(this[offset] & 0x80)) return (this[offset])
		  return ((0xff - this[offset] + 1) * -1)
		};

		Buffer.prototype.readInt16LE = function readInt16LE (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 2, this.length);
		  const val = this[offset] | (this[offset + 1] << 8);
		  return (val & 0x8000) ? val | 0xFFFF0000 : val
		};

		Buffer.prototype.readInt16BE = function readInt16BE (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 2, this.length);
		  const val = this[offset + 1] | (this[offset] << 8);
		  return (val & 0x8000) ? val | 0xFFFF0000 : val
		};

		Buffer.prototype.readInt32LE = function readInt32LE (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 4, this.length);

		  return (this[offset]) |
		    (this[offset + 1] << 8) |
		    (this[offset + 2] << 16) |
		    (this[offset + 3] << 24)
		};

		Buffer.prototype.readInt32BE = function readInt32BE (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 4, this.length);

		  return (this[offset] << 24) |
		    (this[offset + 1] << 16) |
		    (this[offset + 2] << 8) |
		    (this[offset + 3])
		};

		Buffer.prototype.readBigInt64LE = defineBigIntMethod(function readBigInt64LE (offset) {
		  offset = offset >>> 0;
		  validateNumber(offset, 'offset');
		  const first = this[offset];
		  const last = this[offset + 7];
		  if (first === undefined || last === undefined) {
		    boundsError(offset, this.length - 8);
		  }

		  const val = this[offset + 4] +
		    this[offset + 5] * 2 ** 8 +
		    this[offset + 6] * 2 ** 16 +
		    (last << 24); // Overflow

		  return (BigInt(val) << BigInt(32)) +
		    BigInt(first +
		    this[++offset] * 2 ** 8 +
		    this[++offset] * 2 ** 16 +
		    this[++offset] * 2 ** 24)
		});

		Buffer.prototype.readBigInt64BE = defineBigIntMethod(function readBigInt64BE (offset) {
		  offset = offset >>> 0;
		  validateNumber(offset, 'offset');
		  const first = this[offset];
		  const last = this[offset + 7];
		  if (first === undefined || last === undefined) {
		    boundsError(offset, this.length - 8);
		  }

		  const val = (first << 24) + // Overflow
		    this[++offset] * 2 ** 16 +
		    this[++offset] * 2 ** 8 +
		    this[++offset];

		  return (BigInt(val) << BigInt(32)) +
		    BigInt(this[++offset] * 2 ** 24 +
		    this[++offset] * 2 ** 16 +
		    this[++offset] * 2 ** 8 +
		    last)
		});

		Buffer.prototype.readFloatLE = function readFloatLE (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 4, this.length);
		  return ieee754.read(this, offset, true, 23, 4)
		};

		Buffer.prototype.readFloatBE = function readFloatBE (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 4, this.length);
		  return ieee754.read(this, offset, false, 23, 4)
		};

		Buffer.prototype.readDoubleLE = function readDoubleLE (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 8, this.length);
		  return ieee754.read(this, offset, true, 52, 8)
		};

		Buffer.prototype.readDoubleBE = function readDoubleBE (offset, noAssert) {
		  offset = offset >>> 0;
		  if (!noAssert) checkOffset(offset, 8, this.length);
		  return ieee754.read(this, offset, false, 52, 8)
		};

		function checkInt (buf, value, offset, ext, max, min) {
		  if (!Buffer.isBuffer(buf)) throw new TypeError('"buffer" argument must be a Buffer instance')
		  if (value > max || value < min) throw new RangeError('"value" argument is out of bounds')
		  if (offset + ext > buf.length) throw new RangeError('Index out of range')
		}

		Buffer.prototype.writeUintLE =
		Buffer.prototype.writeUIntLE = function writeUIntLE (value, offset, byteLength, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  byteLength = byteLength >>> 0;
		  if (!noAssert) {
		    const maxBytes = Math.pow(2, 8 * byteLength) - 1;
		    checkInt(this, value, offset, byteLength, maxBytes, 0);
		  }

		  let mul = 1;
		  let i = 0;
		  this[offset] = value & 0xFF;
		  while (++i < byteLength && (mul *= 0x100)) {
		    this[offset + i] = (value / mul) & 0xFF;
		  }

		  return offset + byteLength
		};

		Buffer.prototype.writeUintBE =
		Buffer.prototype.writeUIntBE = function writeUIntBE (value, offset, byteLength, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  byteLength = byteLength >>> 0;
		  if (!noAssert) {
		    const maxBytes = Math.pow(2, 8 * byteLength) - 1;
		    checkInt(this, value, offset, byteLength, maxBytes, 0);
		  }

		  let i = byteLength - 1;
		  let mul = 1;
		  this[offset + i] = value & 0xFF;
		  while (--i >= 0 && (mul *= 0x100)) {
		    this[offset + i] = (value / mul) & 0xFF;
		  }

		  return offset + byteLength
		};

		Buffer.prototype.writeUint8 =
		Buffer.prototype.writeUInt8 = function writeUInt8 (value, offset, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) checkInt(this, value, offset, 1, 0xff, 0);
		  this[offset] = (value & 0xff);
		  return offset + 1
		};

		Buffer.prototype.writeUint16LE =
		Buffer.prototype.writeUInt16LE = function writeUInt16LE (value, offset, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) checkInt(this, value, offset, 2, 0xffff, 0);
		  this[offset] = (value & 0xff);
		  this[offset + 1] = (value >>> 8);
		  return offset + 2
		};

		Buffer.prototype.writeUint16BE =
		Buffer.prototype.writeUInt16BE = function writeUInt16BE (value, offset, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) checkInt(this, value, offset, 2, 0xffff, 0);
		  this[offset] = (value >>> 8);
		  this[offset + 1] = (value & 0xff);
		  return offset + 2
		};

		Buffer.prototype.writeUint32LE =
		Buffer.prototype.writeUInt32LE = function writeUInt32LE (value, offset, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) checkInt(this, value, offset, 4, 0xffffffff, 0);
		  this[offset + 3] = (value >>> 24);
		  this[offset + 2] = (value >>> 16);
		  this[offset + 1] = (value >>> 8);
		  this[offset] = (value & 0xff);
		  return offset + 4
		};

		Buffer.prototype.writeUint32BE =
		Buffer.prototype.writeUInt32BE = function writeUInt32BE (value, offset, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) checkInt(this, value, offset, 4, 0xffffffff, 0);
		  this[offset] = (value >>> 24);
		  this[offset + 1] = (value >>> 16);
		  this[offset + 2] = (value >>> 8);
		  this[offset + 3] = (value & 0xff);
		  return offset + 4
		};

		function wrtBigUInt64LE (buf, value, offset, min, max) {
		  checkIntBI(value, min, max, buf, offset, 7);

		  let lo = Number(value & BigInt(0xffffffff));
		  buf[offset++] = lo;
		  lo = lo >> 8;
		  buf[offset++] = lo;
		  lo = lo >> 8;
		  buf[offset++] = lo;
		  lo = lo >> 8;
		  buf[offset++] = lo;
		  let hi = Number(value >> BigInt(32) & BigInt(0xffffffff));
		  buf[offset++] = hi;
		  hi = hi >> 8;
		  buf[offset++] = hi;
		  hi = hi >> 8;
		  buf[offset++] = hi;
		  hi = hi >> 8;
		  buf[offset++] = hi;
		  return offset
		}

		function wrtBigUInt64BE (buf, value, offset, min, max) {
		  checkIntBI(value, min, max, buf, offset, 7);

		  let lo = Number(value & BigInt(0xffffffff));
		  buf[offset + 7] = lo;
		  lo = lo >> 8;
		  buf[offset + 6] = lo;
		  lo = lo >> 8;
		  buf[offset + 5] = lo;
		  lo = lo >> 8;
		  buf[offset + 4] = lo;
		  let hi = Number(value >> BigInt(32) & BigInt(0xffffffff));
		  buf[offset + 3] = hi;
		  hi = hi >> 8;
		  buf[offset + 2] = hi;
		  hi = hi >> 8;
		  buf[offset + 1] = hi;
		  hi = hi >> 8;
		  buf[offset] = hi;
		  return offset + 8
		}

		Buffer.prototype.writeBigUInt64LE = defineBigIntMethod(function writeBigUInt64LE (value, offset = 0) {
		  return wrtBigUInt64LE(this, value, offset, BigInt(0), BigInt('0xffffffffffffffff'))
		});

		Buffer.prototype.writeBigUInt64BE = defineBigIntMethod(function writeBigUInt64BE (value, offset = 0) {
		  return wrtBigUInt64BE(this, value, offset, BigInt(0), BigInt('0xffffffffffffffff'))
		});

		Buffer.prototype.writeIntLE = function writeIntLE (value, offset, byteLength, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) {
		    const limit = Math.pow(2, (8 * byteLength) - 1);

		    checkInt(this, value, offset, byteLength, limit - 1, -limit);
		  }

		  let i = 0;
		  let mul = 1;
		  let sub = 0;
		  this[offset] = value & 0xFF;
		  while (++i < byteLength && (mul *= 0x100)) {
		    if (value < 0 && sub === 0 && this[offset + i - 1] !== 0) {
		      sub = 1;
		    }
		    this[offset + i] = ((value / mul) >> 0) - sub & 0xFF;
		  }

		  return offset + byteLength
		};

		Buffer.prototype.writeIntBE = function writeIntBE (value, offset, byteLength, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) {
		    const limit = Math.pow(2, (8 * byteLength) - 1);

		    checkInt(this, value, offset, byteLength, limit - 1, -limit);
		  }

		  let i = byteLength - 1;
		  let mul = 1;
		  let sub = 0;
		  this[offset + i] = value & 0xFF;
		  while (--i >= 0 && (mul *= 0x100)) {
		    if (value < 0 && sub === 0 && this[offset + i + 1] !== 0) {
		      sub = 1;
		    }
		    this[offset + i] = ((value / mul) >> 0) - sub & 0xFF;
		  }

		  return offset + byteLength
		};

		Buffer.prototype.writeInt8 = function writeInt8 (value, offset, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) checkInt(this, value, offset, 1, 0x7f, -128);
		  if (value < 0) value = 0xff + value + 1;
		  this[offset] = (value & 0xff);
		  return offset + 1
		};

		Buffer.prototype.writeInt16LE = function writeInt16LE (value, offset, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) checkInt(this, value, offset, 2, 0x7fff, -32768);
		  this[offset] = (value & 0xff);
		  this[offset + 1] = (value >>> 8);
		  return offset + 2
		};

		Buffer.prototype.writeInt16BE = function writeInt16BE (value, offset, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) checkInt(this, value, offset, 2, 0x7fff, -32768);
		  this[offset] = (value >>> 8);
		  this[offset + 1] = (value & 0xff);
		  return offset + 2
		};

		Buffer.prototype.writeInt32LE = function writeInt32LE (value, offset, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) checkInt(this, value, offset, 4, 0x7fffffff, -2147483648);
		  this[offset] = (value & 0xff);
		  this[offset + 1] = (value >>> 8);
		  this[offset + 2] = (value >>> 16);
		  this[offset + 3] = (value >>> 24);
		  return offset + 4
		};

		Buffer.prototype.writeInt32BE = function writeInt32BE (value, offset, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) checkInt(this, value, offset, 4, 0x7fffffff, -2147483648);
		  if (value < 0) value = 0xffffffff + value + 1;
		  this[offset] = (value >>> 24);
		  this[offset + 1] = (value >>> 16);
		  this[offset + 2] = (value >>> 8);
		  this[offset + 3] = (value & 0xff);
		  return offset + 4
		};

		Buffer.prototype.writeBigInt64LE = defineBigIntMethod(function writeBigInt64LE (value, offset = 0) {
		  return wrtBigUInt64LE(this, value, offset, -BigInt('0x8000000000000000'), BigInt('0x7fffffffffffffff'))
		});

		Buffer.prototype.writeBigInt64BE = defineBigIntMethod(function writeBigInt64BE (value, offset = 0) {
		  return wrtBigUInt64BE(this, value, offset, -BigInt('0x8000000000000000'), BigInt('0x7fffffffffffffff'))
		});

		function checkIEEE754 (buf, value, offset, ext, max, min) {
		  if (offset + ext > buf.length) throw new RangeError('Index out of range')
		  if (offset < 0) throw new RangeError('Index out of range')
		}

		function writeFloat (buf, value, offset, littleEndian, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) {
		    checkIEEE754(buf, value, offset, 4);
		  }
		  ieee754.write(buf, value, offset, littleEndian, 23, 4);
		  return offset + 4
		}

		Buffer.prototype.writeFloatLE = function writeFloatLE (value, offset, noAssert) {
		  return writeFloat(this, value, offset, true, noAssert)
		};

		Buffer.prototype.writeFloatBE = function writeFloatBE (value, offset, noAssert) {
		  return writeFloat(this, value, offset, false, noAssert)
		};

		function writeDouble (buf, value, offset, littleEndian, noAssert) {
		  value = +value;
		  offset = offset >>> 0;
		  if (!noAssert) {
		    checkIEEE754(buf, value, offset, 8);
		  }
		  ieee754.write(buf, value, offset, littleEndian, 52, 8);
		  return offset + 8
		}

		Buffer.prototype.writeDoubleLE = function writeDoubleLE (value, offset, noAssert) {
		  return writeDouble(this, value, offset, true, noAssert)
		};

		Buffer.prototype.writeDoubleBE = function writeDoubleBE (value, offset, noAssert) {
		  return writeDouble(this, value, offset, false, noAssert)
		};

		// copy(targetBuffer, targetStart=0, sourceStart=0, sourceEnd=buffer.length)
		Buffer.prototype.copy = function copy (target, targetStart, start, end) {
		  if (!Buffer.isBuffer(target)) throw new TypeError('argument should be a Buffer')
		  if (!start) start = 0;
		  if (!end && end !== 0) end = this.length;
		  if (targetStart >= target.length) targetStart = target.length;
		  if (!targetStart) targetStart = 0;
		  if (end > 0 && end < start) end = start;

		  // Copy 0 bytes; we're done
		  if (end === start) return 0
		  if (target.length === 0 || this.length === 0) return 0

		  // Fatal error conditions
		  if (targetStart < 0) {
		    throw new RangeError('targetStart out of bounds')
		  }
		  if (start < 0 || start >= this.length) throw new RangeError('Index out of range')
		  if (end < 0) throw new RangeError('sourceEnd out of bounds')

		  // Are we oob?
		  if (end > this.length) end = this.length;
		  if (target.length - targetStart < end - start) {
		    end = target.length - targetStart + start;
		  }

		  const len = end - start;

		  if (this === target && typeof Uint8Array.prototype.copyWithin === 'function') {
		    // Use built-in when available, missing from IE11
		    this.copyWithin(targetStart, start, end);
		  } else {
		    Uint8Array.prototype.set.call(
		      target,
		      this.subarray(start, end),
		      targetStart
		    );
		  }

		  return len
		};

		// Usage:
		//    buffer.fill(number[, offset[, end]])
		//    buffer.fill(buffer[, offset[, end]])
		//    buffer.fill(string[, offset[, end]][, encoding])
		Buffer.prototype.fill = function fill (val, start, end, encoding) {
		  // Handle string cases:
		  if (typeof val === 'string') {
		    if (typeof start === 'string') {
		      encoding = start;
		      start = 0;
		      end = this.length;
		    } else if (typeof end === 'string') {
		      encoding = end;
		      end = this.length;
		    }
		    if (encoding !== undefined && typeof encoding !== 'string') {
		      throw new TypeError('encoding must be a string')
		    }
		    if (typeof encoding === 'string' && !Buffer.isEncoding(encoding)) {
		      throw new TypeError('Unknown encoding: ' + encoding)
		    }
		    if (val.length === 1) {
		      const code = val.charCodeAt(0);
		      if ((encoding === 'utf8' && code < 128) ||
		          encoding === 'latin1') {
		        // Fast path: If `val` fits into a single byte, use that numeric value.
		        val = code;
		      }
		    }
		  } else if (typeof val === 'number') {
		    val = val & 255;
		  } else if (typeof val === 'boolean') {
		    val = Number(val);
		  }

		  // Invalid ranges are not set to a default, so can range check early.
		  if (start < 0 || this.length < start || this.length < end) {
		    throw new RangeError('Out of range index')
		  }

		  if (end <= start) {
		    return this
		  }

		  start = start >>> 0;
		  end = end === undefined ? this.length : end >>> 0;

		  if (!val) val = 0;

		  let i;
		  if (typeof val === 'number') {
		    for (i = start; i < end; ++i) {
		      this[i] = val;
		    }
		  } else {
		    const bytes = Buffer.isBuffer(val)
		      ? val
		      : Buffer.from(val, encoding);
		    const len = bytes.length;
		    if (len === 0) {
		      throw new TypeError('The value "' + val +
		        '" is invalid for argument "value"')
		    }
		    for (i = 0; i < end - start; ++i) {
		      this[i + start] = bytes[i % len];
		    }
		  }

		  return this
		};

		// CUSTOM ERRORS
		// =============

		// Simplified versions from Node, changed for Buffer-only usage
		const errors = {};
		function E (sym, getMessage, Base) {
		  errors[sym] = class NodeError extends Base {
		    constructor () {
		      super();

		      Object.defineProperty(this, 'message', {
		        value: getMessage.apply(this, arguments),
		        writable: true,
		        configurable: true
		      });

		      // Add the error code to the name to include it in the stack trace.
		      this.name = `${this.name} [${sym}]`;
		      // Access the stack to generate the error message including the error code
		      // from the name.
		      this.stack; // eslint-disable-line no-unused-expressions
		      // Reset the name to the actual name.
		      delete this.name;
		    }

		    get code () {
		      return sym
		    }

		    set code (value) {
		      Object.defineProperty(this, 'code', {
		        configurable: true,
		        enumerable: true,
		        value,
		        writable: true
		      });
		    }

		    toString () {
		      return `${this.name} [${sym}]: ${this.message}`
		    }
		  };
		}

		E('ERR_BUFFER_OUT_OF_BOUNDS',
		  function (name) {
		    if (name) {
		      return `${name} is outside of buffer bounds`
		    }

		    return 'Attempt to access memory outside buffer bounds'
		  }, RangeError);
		E('ERR_INVALID_ARG_TYPE',
		  function (name, actual) {
		    return `The "${name}" argument must be of type number. Received type ${typeof actual}`
		  }, TypeError);
		E('ERR_OUT_OF_RANGE',
		  function (str, range, input) {
		    let msg = `The value of "${str}" is out of range.`;
		    let received = input;
		    if (Number.isInteger(input) && Math.abs(input) > 2 ** 32) {
		      received = addNumericalSeparator(String(input));
		    } else if (typeof input === 'bigint') {
		      received = String(input);
		      if (input > BigInt(2) ** BigInt(32) || input < -(BigInt(2) ** BigInt(32))) {
		        received = addNumericalSeparator(received);
		      }
		      received += 'n';
		    }
		    msg += ` It must be ${range}. Received ${received}`;
		    return msg
		  }, RangeError);

		function addNumericalSeparator (val) {
		  let res = '';
		  let i = val.length;
		  const start = val[0] === '-' ? 1 : 0;
		  for (; i >= start + 4; i -= 3) {
		    res = `_${val.slice(i - 3, i)}${res}`;
		  }
		  return `${val.slice(0, i)}${res}`
		}

		// CHECK FUNCTIONS
		// ===============

		function checkBounds (buf, offset, byteLength) {
		  validateNumber(offset, 'offset');
		  if (buf[offset] === undefined || buf[offset + byteLength] === undefined) {
		    boundsError(offset, buf.length - (byteLength + 1));
		  }
		}

		function checkIntBI (value, min, max, buf, offset, byteLength) {
		  if (value > max || value < min) {
		    const n = typeof min === 'bigint' ? 'n' : '';
		    let range;
		    {
		      if (min === 0 || min === BigInt(0)) {
		        range = `>= 0${n} and < 2${n} ** ${(byteLength + 1) * 8}${n}`;
		      } else {
		        range = `>= -(2${n} ** ${(byteLength + 1) * 8 - 1}${n}) and < 2 ** ` +
		                `${(byteLength + 1) * 8 - 1}${n}`;
		      }
		    }
		    throw new errors.ERR_OUT_OF_RANGE('value', range, value)
		  }
		  checkBounds(buf, offset, byteLength);
		}

		function validateNumber (value, name) {
		  if (typeof value !== 'number') {
		    throw new errors.ERR_INVALID_ARG_TYPE(name, 'number', value)
		  }
		}

		function boundsError (value, length, type) {
		  if (Math.floor(value) !== value) {
		    validateNumber(value, type);
		    throw new errors.ERR_OUT_OF_RANGE('offset', 'an integer', value)
		  }

		  if (length < 0) {
		    throw new errors.ERR_BUFFER_OUT_OF_BOUNDS()
		  }

		  throw new errors.ERR_OUT_OF_RANGE('offset',
		                                    `>= ${0} and <= ${length}`,
		                                    value)
		}

		// HELPER FUNCTIONS
		// ================

		const INVALID_BASE64_RE = /[^+/0-9A-Za-z-_]/g;

		function base64clean (str) {
		  // Node takes equal signs as end of the Base64 encoding
		  str = str.split('=')[0];
		  // Node strips out invalid characters like \n and \t from the string, base64-js does not
		  str = str.trim().replace(INVALID_BASE64_RE, '');
		  // Node converts strings with length < 2 to ''
		  if (str.length < 2) return ''
		  // Node allows for non-padded base64 strings (missing trailing ===), base64-js does not
		  while (str.length % 4 !== 0) {
		    str = str + '=';
		  }
		  return str
		}

		function utf8ToBytes (string, units) {
		  units = units || Infinity;
		  let codePoint;
		  const length = string.length;
		  let leadSurrogate = null;
		  const bytes = [];

		  for (let i = 0; i < length; ++i) {
		    codePoint = string.charCodeAt(i);

		    // is surrogate component
		    if (codePoint > 0xD7FF && codePoint < 0xE000) {
		      // last char was a lead
		      if (!leadSurrogate) {
		        // no lead yet
		        if (codePoint > 0xDBFF) {
		          // unexpected trail
		          if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
		          continue
		        } else if (i + 1 === length) {
		          // unpaired lead
		          if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
		          continue
		        }

		        // valid lead
		        leadSurrogate = codePoint;

		        continue
		      }

		      // 2 leads in a row
		      if (codePoint < 0xDC00) {
		        if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
		        leadSurrogate = codePoint;
		        continue
		      }

		      // valid surrogate pair
		      codePoint = (leadSurrogate - 0xD800 << 10 | codePoint - 0xDC00) + 0x10000;
		    } else if (leadSurrogate) {
		      // valid bmp char, but last char was a lead
		      if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
		    }

		    leadSurrogate = null;

		    // encode utf8
		    if (codePoint < 0x80) {
		      if ((units -= 1) < 0) break
		      bytes.push(codePoint);
		    } else if (codePoint < 0x800) {
		      if ((units -= 2) < 0) break
		      bytes.push(
		        codePoint >> 0x6 | 0xC0,
		        codePoint & 0x3F | 0x80
		      );
		    } else if (codePoint < 0x10000) {
		      if ((units -= 3) < 0) break
		      bytes.push(
		        codePoint >> 0xC | 0xE0,
		        codePoint >> 0x6 & 0x3F | 0x80,
		        codePoint & 0x3F | 0x80
		      );
		    } else if (codePoint < 0x110000) {
		      if ((units -= 4) < 0) break
		      bytes.push(
		        codePoint >> 0x12 | 0xF0,
		        codePoint >> 0xC & 0x3F | 0x80,
		        codePoint >> 0x6 & 0x3F | 0x80,
		        codePoint & 0x3F | 0x80
		      );
		    } else {
		      throw new Error('Invalid code point')
		    }
		  }

		  return bytes
		}

		function asciiToBytes (str) {
		  const byteArray = [];
		  for (let i = 0; i < str.length; ++i) {
		    // Node's code seems to be doing this and not & 0x7F..
		    byteArray.push(str.charCodeAt(i) & 0xFF);
		  }
		  return byteArray
		}

		function utf16leToBytes (str, units) {
		  let c, hi, lo;
		  const byteArray = [];
		  for (let i = 0; i < str.length; ++i) {
		    if ((units -= 2) < 0) break

		    c = str.charCodeAt(i);
		    hi = c >> 8;
		    lo = c % 256;
		    byteArray.push(lo);
		    byteArray.push(hi);
		  }

		  return byteArray
		}

		function base64ToBytes (str) {
		  return base64.toByteArray(base64clean(str))
		}

		function blitBuffer (src, dst, offset, length) {
		  let i;
		  for (i = 0; i < length; ++i) {
		    if ((i + offset >= dst.length) || (i >= src.length)) break
		    dst[i + offset] = src[i];
		  }
		  return i
		}

		// ArrayBuffer or Uint8Array objects from other contexts (i.e. iframes) do not pass
		// the `instanceof` check but they should be treated as of that type.
		// See: https://github.com/feross/buffer/issues/166
		function isInstance (obj, type) {
		  return obj instanceof type ||
		    (obj != null && obj.constructor != null && obj.constructor.name != null &&
		      obj.constructor.name === type.name)
		}
		function numberIsNaN (obj) {
		  // For IE11 support
		  return obj !== obj // eslint-disable-line no-self-compare
		}

		// Create lookup table for `toString('hex')`
		// See: https://github.com/feross/buffer/issues/219
		const hexSliceLookupTable = (function () {
		  const alphabet = '0123456789abcdef';
		  const table = new Array(256);
		  for (let i = 0; i < 16; ++i) {
		    const i16 = i * 16;
		    for (let j = 0; j < 16; ++j) {
		      table[i16 + j] = alphabet[i] + alphabet[j];
		    }
		  }
		  return table
		})();

		// Return not function with Error if BigInt not supported
		function defineBigIntMethod (fn) {
		  return typeof BigInt === 'undefined' ? BufferBigIntNotDefined : fn
		}

		function BufferBigIntNotDefined () {
		  throw new Error('BigInt not supported')
		} 
	} (buffer));
	return buffer;
}

var bufferExports = requireBuffer();

var safeBuffer = {exports: {}};

/*! safe-buffer. MIT License. Feross Aboukhadijeh <https://feross.org/opensource> */

var hasRequiredSafeBuffer;

function requireSafeBuffer () {
	if (hasRequiredSafeBuffer) return safeBuffer.exports;
	hasRequiredSafeBuffer = 1;
	(function (module, exports$1) {
		/* eslint-disable node/no-deprecated-api */
		var buffer = requireBuffer();
		var Buffer = buffer.Buffer;

		// alternative to using Object.keys for old browsers
		function copyProps (src, dst) {
		  for (var key in src) {
		    dst[key] = src[key];
		  }
		}
		if (Buffer.from && Buffer.alloc && Buffer.allocUnsafe && Buffer.allocUnsafeSlow) {
		  module.exports = buffer;
		} else {
		  // Copy properties from require('buffer')
		  copyProps(buffer, exports$1);
		  exports$1.Buffer = SafeBuffer;
		}

		function SafeBuffer (arg, encodingOrOffset, length) {
		  return Buffer(arg, encodingOrOffset, length)
		}

		SafeBuffer.prototype = Object.create(Buffer.prototype);

		// Copy static methods from Buffer
		copyProps(Buffer, SafeBuffer);

		SafeBuffer.from = function (arg, encodingOrOffset, length) {
		  if (typeof arg === 'number') {
		    throw new TypeError('Argument must not be a number')
		  }
		  return Buffer(arg, encodingOrOffset, length)
		};

		SafeBuffer.alloc = function (size, fill, encoding) {
		  if (typeof size !== 'number') {
		    throw new TypeError('Argument must be a number')
		  }
		  var buf = Buffer(size);
		  if (fill !== undefined) {
		    if (typeof encoding === 'string') {
		      buf.fill(fill, encoding);
		    } else {
		      buf.fill(fill);
		    }
		  } else {
		    buf.fill(0);
		  }
		  return buf
		};

		SafeBuffer.allocUnsafe = function (size) {
		  if (typeof size !== 'number') {
		    throw new TypeError('Argument must be a number')
		  }
		  return Buffer(size)
		};

		SafeBuffer.allocUnsafeSlow = function (size) {
		  if (typeof size !== 'number') {
		    throw new TypeError('Argument must be a number')
		  }
		  return buffer.SlowBuffer(size)
		}; 
	} (safeBuffer, safeBuffer.exports));
	return safeBuffer.exports;
}

var src;
var hasRequiredSrc;

function requireSrc () {
	if (hasRequiredSrc) return src;
	hasRequiredSrc = 1;
	// base-x encoding / decoding
	// Copyright (c) 2018 base-x contributors
	// Copyright (c) 2014-2018 The Bitcoin Core developers (base58.cpp)
	// Distributed under the MIT software license, see the accompanying
	// file LICENSE or http://www.opensource.org/licenses/mit-license.php.
	// @ts-ignore
	var _Buffer = requireSafeBuffer().Buffer;
	function base (ALPHABET) {
	  if (ALPHABET.length >= 255) { throw new TypeError('Alphabet too long') }
	  var BASE_MAP = new Uint8Array(256);
	  for (var j = 0; j < BASE_MAP.length; j++) {
	    BASE_MAP[j] = 255;
	  }
	  for (var i = 0; i < ALPHABET.length; i++) {
	    var x = ALPHABET.charAt(i);
	    var xc = x.charCodeAt(0);
	    if (BASE_MAP[xc] !== 255) { throw new TypeError(x + ' is ambiguous') }
	    BASE_MAP[xc] = i;
	  }
	  var BASE = ALPHABET.length;
	  var LEADER = ALPHABET.charAt(0);
	  var FACTOR = Math.log(BASE) / Math.log(256); // log(BASE) / log(256), rounded up
	  var iFACTOR = Math.log(256) / Math.log(BASE); // log(256) / log(BASE), rounded up
	  function encode (source) {
	    if (Array.isArray(source) || source instanceof Uint8Array) { source = _Buffer.from(source); }
	    if (!_Buffer.isBuffer(source)) { throw new TypeError('Expected Buffer') }
	    if (source.length === 0) { return '' }
	        // Skip & count leading zeroes.
	    var zeroes = 0;
	    var length = 0;
	    var pbegin = 0;
	    var pend = source.length;
	    while (pbegin !== pend && source[pbegin] === 0) {
	      pbegin++;
	      zeroes++;
	    }
	        // Allocate enough space in big-endian base58 representation.
	    var size = ((pend - pbegin) * iFACTOR + 1) >>> 0;
	    var b58 = new Uint8Array(size);
	        // Process the bytes.
	    while (pbegin !== pend) {
	      var carry = source[pbegin];
	            // Apply "b58 = b58 * 256 + ch".
	      var i = 0;
	      for (var it1 = size - 1; (carry !== 0 || i < length) && (it1 !== -1); it1--, i++) {
	        carry += (256 * b58[it1]) >>> 0;
	        b58[it1] = (carry % BASE) >>> 0;
	        carry = (carry / BASE) >>> 0;
	      }
	      if (carry !== 0) { throw new Error('Non-zero carry') }
	      length = i;
	      pbegin++;
	    }
	        // Skip leading zeroes in base58 result.
	    var it2 = size - length;
	    while (it2 !== size && b58[it2] === 0) {
	      it2++;
	    }
	        // Translate the result into a string.
	    var str = LEADER.repeat(zeroes);
	    for (; it2 < size; ++it2) { str += ALPHABET.charAt(b58[it2]); }
	    return str
	  }
	  function decodeUnsafe (source) {
	    if (typeof source !== 'string') { throw new TypeError('Expected String') }
	    if (source.length === 0) { return _Buffer.alloc(0) }
	    var psz = 0;
	        // Skip and count leading '1's.
	    var zeroes = 0;
	    var length = 0;
	    while (source[psz] === LEADER) {
	      zeroes++;
	      psz++;
	    }
	        // Allocate enough space in big-endian base256 representation.
	    var size = (((source.length - psz) * FACTOR) + 1) >>> 0; // log(58) / log(256), rounded up.
	    var b256 = new Uint8Array(size);
	        // Process the characters.
	    while (psz < source.length) {
	            // Find code of next character
	      var charCode = source.charCodeAt(psz);
	            // Base map can not be indexed using char code
	      if (charCode > 255) { return }
	            // Decode character
	      var carry = BASE_MAP[charCode];
	            // Invalid character
	      if (carry === 255) { return }
	      var i = 0;
	      for (var it3 = size - 1; (carry !== 0 || i < length) && (it3 !== -1); it3--, i++) {
	        carry += (BASE * b256[it3]) >>> 0;
	        b256[it3] = (carry % 256) >>> 0;
	        carry = (carry / 256) >>> 0;
	      }
	      if (carry !== 0) { throw new Error('Non-zero carry') }
	      length = i;
	      psz++;
	    }
	        // Skip leading zeroes in b256.
	    var it4 = size - length;
	    while (it4 !== size && b256[it4] === 0) {
	      it4++;
	    }
	    var vch = _Buffer.allocUnsafe(zeroes + (size - it4));
	    vch.fill(0x00, 0, zeroes);
	    var j = zeroes;
	    while (it4 !== size) {
	      vch[j++] = b256[it4++];
	    }
	    return vch
	  }
	  function decode (string) {
	    var buffer = decodeUnsafe(string);
	    if (buffer) { return buffer }
	    throw new Error('Non-base' + BASE + ' character')
	  }
	  return {
	    encode: encode,
	    decodeUnsafe: decodeUnsafe,
	    decode: decode
	  }
	}
	src = base;
	return src;
}

var bs58$1;
var hasRequiredBs58;

function requireBs58 () {
	if (hasRequiredBs58) return bs58$1;
	hasRequiredBs58 = 1;
	var basex = requireSrc();
	var ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

	bs58$1 = basex(ALPHABET);
	return bs58$1;
}

var bs58Exports = requireBs58();
var bs58 = /*@__PURE__*/getDefaultExportFromCjs(bs58Exports);

// ─────────────────────────────────────────────────────────────
// Local implementation of getSimulationComputeUnits
// (Replaces @solana-developers/helpers to avoid Wallet import issue in browser/ESM builds)
// Source: https://github.com/solana-developers/helpers/blob/main/src/lib/transaction.ts
// ─────────────────────────────────────────────────────────────
/**
 * Check if a given instruction is a SetComputeUnitLimit instruction
 * See https://github.com/solana-program/compute-budget/blob/main/clients/js/src/generated/programs/computeBudget.ts#L29
 */
function isSetComputeLimitInstruction(ix) {
    return (ix.programId.equals(ComputeBudgetProgram.programId) &&
        ix.data[0] === 2 // opcode for setComputeUnitLimit is 2
    );
}
/**
 * Get the compute units required for a set of instructions via simulation.
 * Credit https://twitter.com/stegabob, originally from https://x.com/stegaBOB/status/1766662289392889920
 */
async function getSimulationComputeUnits(connection, instructions, payer, lookupTables, commitment = "confirmed") {
    var _a, _b, _c;
    const simulationInstructions = [...instructions];
    // Replace or add compute limit instruction
    const computeLimitIndex = simulationInstructions.findIndex(isSetComputeLimitInstruction);
    const simulationLimitIx = ComputeBudgetProgram.setComputeUnitLimit({
        units: 1400000,
    });
    if (computeLimitIndex >= 0) {
        simulationInstructions[computeLimitIndex] = simulationLimitIx;
    }
    else {
        simulationInstructions.unshift(simulationLimitIx);
    }
    const testTransaction = new VersionedTransaction(new TransactionMessage({
        instructions: simulationInstructions,
        payerKey: payer,
        // RecentBlockhash can by any public key during simulation
        // since 'replaceRecentBlockhash' is set to 'true' below
        recentBlockhash: PublicKey.default.toString(),
    }).compileToV0Message(lookupTables));
    const rpcResponse = await connection.simulateTransaction(testTransaction, {
        replaceRecentBlockhash: true,
        sigVerify: false,
        commitment,
    });
    if ((_a = rpcResponse === null || rpcResponse === void 0 ? void 0 : rpcResponse.value) === null || _a === void 0 ? void 0 : _a.err) {
        const logs = ((_b = rpcResponse.value.logs) === null || _b === void 0 ? void 0 : _b.join("\n  • ")) || "No logs available";
        throw new Error(`Transaction simulation failed:\n  •${logs}` +
            JSON.stringify((_c = rpcResponse === null || rpcResponse === void 0 ? void 0 : rpcResponse.value) === null || _c === void 0 ? void 0 : _c.err));
    }
    return rpcResponse.value.unitsConsumed || null;
}
/* ------------------------------------------------------------------ */
/*  RFC-4501 message                                                  */
/* ------------------------------------------------------------------ */
async function genSolanaMessage(address, nonce) {
    var _a, _b;
    /*  Domain / origin aren't available server-side – fall back to envs
        or simple placeholders.  */
    const domain = (typeof process !== 'undefined' && ((_a = process.env) === null || _a === void 0 ? void 0 : _a.SESSION_DOMAIN)) || "server";
    const origin = (typeof process !== 'undefined' && ((_b = process.env) === null || _b === void 0 ? void 0 : _b.SESSION_ORIGIN)) || "server";
    const statement = "Sign this message to authenticate with our application.";
    const currentDate = new Date();
    const issuedAt = currentDate.toISOString();
    const expirationDate = new Date(currentDate.getTime() + 5 * 60 * 1000);
    const expirationTime = expirationDate.toISOString();
    return `${statement}\n\n` +
        `Wallet address:\n${address}\n\n` +
        `Domain: ${domain}\n` +
        `Origin: ${origin}\n` +
        `Nonce: ${nonce}\n` +
        `Issued At: ${issuedAt}\n` +
        `Expiration Time: ${expirationTime}`;
}
// ─────────────────────────────────────────────────────────────
// Serialization Helpers
// ─────────────────────────────────────────────────────────────
function isHexString(value) {
    // Only strings can be hex-encoded. Numbers, BNs, and other shapes coming back
    // from the API for u64Val/i64Val should fall through to `new BN(value)` which
    // accepts those shapes natively. Without this guard, calling .startsWith on a
    // non-string blows up with TypeError.
    if (typeof value !== "string")
        return false;
    // Matches strings containing only 0-9, a-f, or A-F (optionally prefixed with 0x)
    let testValue = value;
    // Handle negative values
    if (value.startsWith("-")) {
        testValue = value.slice(1);
    }
    const hexRegex = /^(0x)?[0-9a-fA-F]+$/;
    return hexRegex.test(testValue);
}
function prepareAnchorArgs(documents) {
    return documents.map((doc) => ({
        path: doc.path,
        operations: doc.operations.map((op) => {
            let preparedValue = null;
            if (op.value) {
                if ("u64Val" in op.value) {
                    if (isHexString(op.value.u64Val.value)) {
                        preparedValue = { u64Val: { value: new BN(op.value.u64Val.value, "hex") } };
                    }
                    else {
                        preparedValue = { u64Val: { value: new BN(op.value.u64Val.value) } };
                    }
                }
                else if ("i64Val" in op.value) {
                    if (isHexString(op.value.i64Val.value)) {
                        if (op.value.i64Val.value.startsWith("-")) {
                            preparedValue = { i64Val: { value: new BN(op.value.i64Val.value.slice(1), "hex").mul(new BN(-1)) } };
                        }
                        else {
                            preparedValue = { i64Val: { value: new BN(op.value.i64Val.value, "hex") } };
                        }
                    }
                    else {
                        preparedValue = { i64Val: { value: new BN(op.value.i64Val.value) } };
                    }
                }
                else if (op.value.boolVal) {
                    preparedValue = { boolVal: { value: !!op.value.boolVal.value } };
                }
                else if (op.value.stringVal) {
                    preparedValue = { stringVal: { value: String(op.value.stringVal.value) } };
                }
                else if (op.value.addressVal) {
                    preparedValue = { addressVal: { value: new PublicKey(op.value.addressVal.value) } };
                }
                else {
                    preparedValue = op.value;
                }
            }
            return {
                key: op.key,
                value: preparedValue,
                operation: op.operation,
            };
        }),
    }));
}
function convertRemainingAccounts(remainingAccounts) {
    return remainingAccounts.map(acc => {
        return {
            pubkey: new PublicKey(acc.pubkey),
            isWritable: acc.isWritable,
            isSigner: acc.isSigner
        };
    });
}
// ─────────────────────────────────────────────────────────────
// Updated Transaction Builder: It now accepts a PublicKey for the payer
// ─────────────────────────────────────────────────────────────
async function buildSetDocumentsTransaction(connection, idl, anchorProvider, payerPublicKey, args, remainingAccounts, lutKey, preInstructions, simulate, additionalLutAddresses) {
    const computeBudgetIx = ComputeBudgetProgram.setComputeUnitLimit({
        units: 1400000,
    });
    //TODO: Figure out long term solution to estimate heap frame size
    const requestHeapFrameIx = ComputeBudgetProgram.requestHeapFrame({
        bytes: 1024 * 160,
    });
    const instructions = [];
    instructions.unshift(requestHeapFrameIx);
    instructions.unshift(computeBudgetIx);
    instructions.push(...preInstructions);
    let tx = null;
    if (idl.address === "taro6CvKqwrYrDc16ufYgzQ2NZcyyVKStffbtudrhRu") {
        const program = new Program(idl, anchorProvider);
        tx = await program.methods
            .setDocuments(args.app_id, prepareAnchorArgs(args.documents), args.delete_paths, simulate)
            .preInstructions(instructions)
            .accounts({
            payer: payerPublicKey
        })
            .remainingAccounts(remainingAccounts)
            .transaction();
    }
    else if (idl.address === "poof4b5pk1L9tmThvBmaABjcyjfhFGbMbQP5BXk2QZp") {
        const program = new Program(idl, anchorProvider);
        // Targets `set_documents_v2` (Vec<u8> ra_indices). The on-chain program
        // also exposes the legacy `set_documents` (Vec<u64>) so SDKs already on npm
        // continue to work — but new releases of this SDK speak v2 only.
        tx = await program.methods
            .setDocumentsV2(args.app_id, prepareAnchorArgs(args.documents), args.delete_paths, args.txData, simulate)
            .preInstructions(instructions)
            .accounts({
            payer: payerPublicKey
        })
            .remainingAccounts(remainingAccounts)
            .transaction();
    }
    else {
        throw new Error(`Invalid program id: ${idl.address}`);
    }
    tx.feePayer = payerPublicKey;
    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash("confirmed");
    tx.recentBlockhash = blockhash;
    // Resolve LUTs: additionalLutAddresses (when present) is the authoritative set.
    // Falls back to lutKey alone for backwards compatibility.
    const lookupTables = [];
    const isSurfnet = anchorProvider.connection.rpcEndpoint == "https://surfpool.fly.dev";
    if (additionalLutAddresses && additionalLutAddresses.length > 0) {
        const results = await Promise.all(additionalLutAddresses.map(addr => connection.getAddressLookupTable(new PublicKey(addr)).catch(() => null)));
        for (const result of results) {
            if (result === null || result === void 0 ? void 0 : result.value) {
                lookupTables.push(result.value);
            }
        }
    }
    else if (lutKey != null) {
        // The LUT may have just been created server-side and the client's RPC node
        // may not have indexed it yet (load-balancer split). Retry with exponential
        // back-off; first retry at 100 ms almost always resolves it.
        let table = null;
        for (let attempt = 0; attempt < 5; attempt++) {
            const { value } = await connection.getAddressLookupTable(new PublicKey(lutKey));
            if (value) {
                table = value;
                break;
            }
            if (attempt < 4)
                await new Promise(r => setTimeout(r, 100 * Math.pow(2, attempt)));
        }
        if (!table)
            throw new Error('LUT not found after creation/extend');
        lookupTables.push(table);
    }
    if (lookupTables.length === 0) {
        const computeUnits = isSurfnet ? 1400000 : await getSimulationComputeUnits(connection, tx.instructions, payerPublicKey, []);
        const computeBudgetIxOptimized = ComputeBudgetProgram.setComputeUnitLimit({
            units: computeUnits ? computeUnits * 1.2 : 1400000,
        }); // 20% buffer
        tx.instructions[0] = computeBudgetIxOptimized;
        return { tx, blockhash, lastValidBlockHeight };
    }
    const computeUnits = isSurfnet ? 1400000 : await getSimulationComputeUnits(connection, tx.instructions, payerPublicKey, lookupTables);
    const computeBudgetIxOptimized = ComputeBudgetProgram.setComputeUnitLimit({
        units: computeUnits ? computeUnits * 1.2 : 1400000,
    }); // 20% buffer
    tx.instructions[0] = computeBudgetIxOptimized;
    const msgV0 = new anchor.web3.TransactionMessage({
        payerKey: payerPublicKey,
        recentBlockhash: blockhash,
        instructions: tx.instructions,
    }).compileToV0Message(lookupTables);
    const vTx = new anchor.web3.VersionedTransaction(msgV0);
    return { tx: vTx, blockhash, lastValidBlockHeight };
}

/* ------------------------------------------------------------------ */
/*  ENV helpers                                                       */
/* ------------------------------------------------------------------ */
const ENV_KEYPAIR = "TAROBASE_SOLANA_KEYPAIR"; // required
function loadKeypairFromEnv() {
    const secret = process.env[ENV_KEYPAIR];
    if (!secret) {
        throw new Error(`Missing ${ENV_KEYPAIR}. Export a base-58 or JSON array secret key.`);
    }
    try {
        const secretKey = secret.trim().startsWith("[")
            ? Uint8Array.from(JSON.parse(secret))
            : bs58.decode(secret.trim());
        return Keypair.fromSecretKey(secretKey);
    }
    catch (err) {
        throw new Error(`Unable to parse ${ENV_KEYPAIR}. Ensure it is valid base-58 or JSON.`);
    }
}
/* ------------------------------------------------------------------ */
/* SESSION MANAGER                                                     */
/* ------------------------------------------------------------------ */
class ServerSessionManager {
    constructor(keypair) {
        /* Private cache (lives for the life of the instance) */
        this.session = null;
        /* Coalesce concurrent getSession() calls into a single in-flight request */
        this.pendingSession = null;
        this.keypair = keypair !== null && keypair !== void 0 ? keypair : null;
    }
    /**
     * Create a session manager for a specific keypair.
     * Each instance has its own independent session cache.
     */
    static forKeypair(keypair) {
        return new ServerSessionManager(keypair);
    }
    /* ---------------------------------------------- *
     * Session creation (instance method)
     * ---------------------------------------------- */
    async createSession() {
        var _a;
        const kp = (_a = this.keypair) !== null && _a !== void 0 ? _a : loadKeypairFromEnv();
        const address = kp.publicKey.toBase58();
        /* fetch nonce from auth API */
        const nonce = await genAuthNonce();
        const message = await genSolanaMessage(address, nonce);
        /* sign the message */
        const sigBytes = nacl.sign.detached(new TextEncoder().encode(message), kp.secretKey);
        const signature = bufferExports.Buffer.from(sigBytes).toString("base64");
        /* call auth API */
        const { accessToken, idToken, refreshToken, } = await createSessionWithSignature(address, message, signature);
        return { address, accessToken, idToken, refreshToken };
    }
    /* ---------------------------------------------- *
     * GET (lazy-fetch)
     * ---------------------------------------------- */
    async getSession() {
        if (this.session !== null) {
            return this.session;
        }
        // If a session creation is already in-flight, reuse that promise
        // instead of firing a second concurrent request.
        if (this.pendingSession !== null) {
            return this.pendingSession;
        }
        this.pendingSession = this.createSession()
            .then((session) => {
            this.session = session;
            return session;
        })
            .finally(() => {
            this.pendingSession = null;
        });
        return this.pendingSession;
    }
    /* ---------------------------------------------- *
     * STORE  (overwrites the cached value)
     * ---------------------------------------------- */
    setSession(session) {
        this.session = session;
    }
    /* ---------------------------------------------- *
     * CLEAR  (e.g. after logout or 401 retry)
     * ---------------------------------------------- */
    clearSession() {
        this.session = null;
        this.pendingSession = null;
    }
    /* ---------------------------------------------- *
     * QUICK helpers
     * ---------------------------------------------- */
    isAuthenticated() {
        return this.session !== null;
    }
    getIdToken() {
        var _a, _b;
        return (_b = (_a = this.session) === null || _a === void 0 ? void 0 : _a.idToken) !== null && _b !== void 0 ? _b : null;
    }
    getRefreshToken() {
        var _a, _b;
        return (_b = (_a = this.session) === null || _a === void 0 ? void 0 : _a.refreshToken) !== null && _b !== void 0 ? _b : null;
    }
}
/* The default singleton instance (reads keypair from env) */
ServerSessionManager.instance = new ServerSessionManager();

var serverSessionManager = /*#__PURE__*/Object.freeze({
	__proto__: null,
	ServerSessionManager: ServerSessionManager
});

/**
 * Safe base64 helpers for tarobase-core.
 *
 * Uses the global atob/btoa when available (browser, RN with polyfill),
 * falls back to the 'buffer' package (Node.js / SSR / RN without polyfill).
 *
 * These are used instead of bare `atob()`/`btoa()` so the core package
 * works in React Native without requiring the consumer to polyfill globals.
 */
function safeAtob(input) {
    if (typeof globalThis.atob === 'function') {
        return globalThis.atob(input);
    }
    return bufferExports.Buffer.from(input, 'base64').toString('binary');
}
function safeBtoa(input) {
    if (typeof globalThis.btoa === 'function') {
        return globalThis.btoa(input);
    }
    return bufferExports.Buffer.from(input, 'binary').toString('base64');
}
/**
 * Decode a base64url-encoded string (used by JWT payloads).
 * Normalises base64url → standard base64 before decoding.
 */
function decodeBase64Url(input) {
    let b64 = input.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad === 2)
        b64 += '==';
    else if (pad === 3)
        b64 += '=';
    return safeAtob(b64);
}

async function createBearerToken(isServer) {
    if (isServer) {
        const sessionMgr = ServerSessionManager.instance;
        const session = await sessionMgr.getSession();
        if (!(session === null || session === void 0 ? void 0 : session.idToken)) {
            return null;
        }
        return `Bearer ${session.idToken}`;
    }
    const idToken = WebSessionManager.getIdToken();
    if (!idToken) {
        return null;
    }
    return `Bearer ${idToken}`;
}
async function createAuthHeader(isServer) {
    const bearerToken = await createBearerToken(isServer);
    if (!bearerToken) {
        return null;
    }
    return {
        Authorization: bearerToken
    };
}
async function getIdToken(isServer) {
    if (isServer) {
        const sessionMgr = ServerSessionManager.instance;
        const session = await sessionMgr.getSession();
        return session === null || session === void 0 ? void 0 : session.idToken;
    }
    return WebSessionManager.getIdToken();
}
async function getRefreshToken(isServer) {
    if (isServer) {
        const sessionMgr = ServerSessionManager.instance;
        const session = await sessionMgr.getSession();
        return session === null || session === void 0 ? void 0 : session.refreshToken;
    }
    return WebSessionManager.getRefreshToken();
}
async function updateIdTokenAndAccessToken(idToken, accessToken, isServer = false) {
    if (isServer) {
        const sessionMgr = ServerSessionManager.instance;
        // Ensure we have a session to update; if missing, let lazy creation happen on next request.
        let session = null;
        try {
            session = await sessionMgr.getSession();
        }
        catch (_a) {
            return;
        }
        if (!session) {
            return;
        }
        sessionMgr.setSession(Object.assign(Object.assign({}, session), { idToken,
            accessToken }));
        return;
    }
    await WebSessionManager.updateIdTokenAndAccessToken(idToken, accessToken);
}

const apiClient = axios.create();
axiosRetry(apiClient, {
    retries: 2,
    retryCondition: (error) => {
        var _a, _b;
        // Only retry GET requests on network errors (ECONNRESET, ETIMEDOUT, etc.)
        // Writes (POST/PUT) are not retried — the server may have processed
        // the request before the connection was reset.
        return axiosRetry.isNetworkError(error) && ((_b = (_a = error.config) === null || _a === void 0 ? void 0 : _a.method) === null || _b === void 0 ? void 0 : _b.toLowerCase()) === 'get';
    },
    retryDelay: axiosRetry.exponentialDelay,
    shouldResetTimeout: true,
    onRetry: (retryCount, error, requestConfig) => {
        var _a;
        console.warn(`[tarobase-sdk] retry ${retryCount} for ${(_a = requestConfig.method) === null || _a === void 0 ? void 0 : _a.toUpperCase()} ${requestConfig.url} — ${error.code || error.message}`);
    },
});
const refreshInFlight = new Map();
async function refreshAuthSessionOnce(appId, isServer) {
    const key = `${isServer ? "server" : "web"}:${appId}`;
    const existing = refreshInFlight.get(key);
    if (existing) {
        return existing;
    }
    const refreshPromise = (async () => {
        var _a, _b;
        try {
            const refreshToken = await getRefreshToken(isServer);
            if (!refreshToken) {
                return false;
            }
            const refreshData = await refreshSession(refreshToken);
            if (!(refreshData === null || refreshData === void 0 ? void 0 : refreshData.idToken) || !(refreshData === null || refreshData === void 0 ? void 0 : refreshData.accessToken)) {
                return false;
            }
            await updateIdTokenAndAccessToken(refreshData.idToken, refreshData.accessToken, isServer);
            return true;
        }
        catch (error) {
            if (!isServer && (((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) === 401 || ((_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.status) === 403)) {
                const { WebSessionManager } = await Promise.resolve().then(function () { return webSessionManager; });
                WebSessionManager.clearSession();
            }
            return false;
        }
        finally {
            refreshInFlight.delete(key);
        }
    })();
    refreshInFlight.set(key, refreshPromise);
    return refreshPromise;
}
async function makeApiRequest(method, urlPath, data, _overrides) {
    var _a, _b, _c, _d;
    const config = await getConfig();
    let hasRetriedAfterServerSessionReset = false;
    const clearServerSession = async () => {
        if (!config.isServer)
            return;
        const { ServerSessionManager } = await Promise.resolve().then(function () { return serverSessionManager; });
        ServerSessionManager.instance.clearSession();
    };
    async function executeRequest() {
        var _a;
        // When _getAuthHeaders is provided (wallet client), use it as the sole auth source.
        // Otherwise use the global createAuthHeader (default path).
        const authHeader = (_overrides === null || _overrides === void 0 ? void 0 : _overrides._getAuthHeaders)
            ? await _overrides._getAuthHeaders()
            : await createAuthHeader(config.isServer);
        const headers = Object.assign({ "Content-Type": "application/json", "X-Public-App-Id": config.appId, "X-App-Id": config.appId }, authHeader);
        if (typeof window !== "undefined" &&
            window.CUSTOM_TAROBASE_APP_ID_HEADER) {
            const customAppId = window.CUSTOM_TAROBASE_APP_ID_HEADER;
            if (customAppId) {
                headers["X-App-Id"] = customAppId;
            }
        }
        // Apply custom headers from _overrides
        if (_overrides === null || _overrides === void 0 ? void 0 : _overrides.headers) {
            Object.assign(headers, _overrides.headers);
        }
        // Writes (PUT/POST/DELETE) can take significantly longer than reads —
        // server-side they may trigger ad-hoc LUT creation (create + extend +
        // wait-for-finalization, ~15-30s on mainnet). Use a larger default for
        // non-GET so the SDK doesn't time out before client-api responds.
        const isRead = method.toUpperCase() === "GET";
        const defaultTimeout = isRead ? 30000 : 90000;
        const requestConfig = {
            method,
            url: `${config.apiUrl}${urlPath.startsWith("/") ? urlPath : `/${urlPath}`}`,
            headers,
            timeout: (_a = _overrides === null || _overrides === void 0 ? void 0 : _overrides.timeout) !== null && _a !== void 0 ? _a : defaultTimeout,
        };
        if (method !== "GET" && method !== "get") {
            requestConfig.data = data ? JSON.stringify(data) : {};
        }
        const response = await apiClient(requestConfig);
        return { data: response.data, status: response.status, headers: response.headers };
    }
    try {
        return await executeRequest();
    }
    catch (error) {
        if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 401) {
            // Wallet client path: use client-specific retry, skip global fallback entirely
            if (_overrides === null || _overrides === void 0 ? void 0 : _overrides._clearAuth) {
                await _overrides._clearAuth();
                return await executeRequest();
                // If this throws, let it propagate — do NOT clear global ServerSessionManager
            }
            // Default path (no wallet client): refresh global session
            try {
                const refreshed = await refreshAuthSessionOnce(config.appId, config.isServer);
                if (!refreshed) {
                    throw new Error("Unable to refresh auth session");
                }
                return await executeRequest();
            }
            catch (_refreshError) {
                // Server-side fallback: clear global session and retry once
                if (config.isServer && !hasRetriedAfterServerSessionReset) {
                    hasRetriedAfterServerSessionReset = true;
                    await clearServerSession();
                    return await executeRequest();
                }
                throw error;
            }
        }
        if (((_b = error.response) === null || _b === void 0 ? void 0 : _b.status) == 404) {
            return { data: null, status: 404 };
        }
        if ((_c = error.response) === null || _c === void 0 ? void 0 : _c.data) {
            const responseData = typeof error.response.data === 'object'
                ? error.response.data
                : { message: String(error.response.data) };
            const apiError = new Error(responseData.message || error.message || 'Request failed');
            Object.assign(apiError, responseData);
            apiError.statusCode = (_d = error.response) === null || _d === void 0 ? void 0 : _d.status;
            throw apiError;
        }
        throw error;
    }
}

let clientConfig = {
    // User configured settings
    name: '',
    logoUrl: '',
    apiKey: '',
    wsApiUrl: 'wss://api.tarobase.com',
    apiUrl: 'https://api.tarobase.com',
    authApiUrl: 'https://auth.tarobase.com',
    appId: '',
    // Custom data from server
    authMethod: 'phantom',
    chain: '',
    rpcUrl: '',
    skipBackendInit: false,
    authProvider: null,
    isServer: false,
};
let isInitialized = false;
function init(newConfig) {
    return new Promise((resolve, reject) => {
        if (!newConfig.apiKey && !newConfig.appId) {
            reject(new Error('No API key or app ID provided.'));
        }
        clientConfig = Object.assign(Object.assign({}, clientConfig), newConfig);
        if (newConfig.skipBackendInit) {
            isInitialized = true;
            resolve();
            return;
        }
        isInitialized = true;
        makeApiRequest('GET', '/config').then((data) => {
            clientConfig = Object.assign(Object.assign(Object.assign({}, clientConfig), data.data), newConfig);
            isInitialized = true;
            resolve();
        }).catch((error) => {
            reject(new Error("Failed to initialize Tarobase app."));
        });
    });
}
function waitForInitialization() {
    return new Promise((resolve) => {
        if (isInitialized) {
            resolve();
        }
        else {
            const interval = setInterval(() => {
                if (isInitialized) {
                    clearInterval(interval);
                    resolve();
                }
            }, 50);
        }
    });
}
async function getConfig() {
    await waitForInitialization();
    return clientConfig;
}

var __rest = (undefined && undefined.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/**
 * Thrown when a user's wallet doesn't have enough SOL to cover the transaction.
 * Apps can catch this to trigger an onramp/funding flow.
 */
class InsufficientBalanceError extends Error {
    constructor(address, balanceLamports, estimatedCostLamports, deficitLamports, deficitSol) {
        super(`Insufficient balance: wallet ${address} needs ~${deficitSol.toFixed(6)} SOL more to complete this transaction.`);
        this.name = 'InsufficientBalanceError';
        this.address = address;
        this.balanceLamports = balanceLamports;
        this.estimatedCostLamports = estimatedCostLamports;
        this.deficitLamports = deficitLamports;
        this.deficitSol = deficitSol;
    }
}
// Cache for get responses
const getCache = {};
// Track in-flight requests to coalesce multiple identical requests
const pendingRequests = {};
// Default cache TTL (500ms = 0.5 seconds)
const GET_CACHE_TTL = 500; // Adjust this value as needed (in milliseconds)
// Last time we cleaned up the cache
let lastCacheCleanup = Date.now();
function hashForKey$1(value) {
    let h = 5381;
    for (let i = 0; i < value.length; i++) {
        h = ((h << 5) + h + value.charCodeAt(i)) & 0x7fffffff;
    }
    return h.toString(36);
}
/**
 * Validates that a field name is a safe identifier (alphanumeric, underscores, dots for nested paths).
 * Prevents prompt injection via crafted field names.
 */
function validateFieldName(field) {
    if (!/^[a-zA-Z0-9_.]+$/.test(field)) {
        throw new Error(`Invalid field name "${field}". Field names must only contain letters, numbers, underscores, and dots.`);
    }
}
/**
 * Parses a raw aggregation result (e.g. [{ count: 42 }] or [{ _id: null, total: 100 }])
 * into a single numeric value.
 */
function parseAggregateValue(result) {
    if (typeof result === 'number')
        return result;
    if (Array.isArray(result)) {
        if (result.length === 0)
            return 0;
        // Multiple elements — not a collapsed aggregate result
        if (result.length > 1) {
            throw new Error(`Unexpected aggregate result: got array with ${result.length} elements. The AI may have returned full documents instead of an aggregation.`);
        }
        const first = result[0];
        if (typeof first === 'number')
            return first;
        if (first && typeof first === 'object') {
            // $count stage returns { count: N }
            if (typeof first.count === 'number')
                return first.count;
            // $group stage returns { _id: null, result: N } — expect _id + one numeric field
            const numericEntries = Object.entries(first).filter(([key, val]) => key !== '_id' && typeof val === 'number');
            if (numericEntries.length === 1)
                return numericEntries[0][1];
        }
        // Avoid leaking document contents into error messages
        const shape = first && typeof first === 'object' ? `{${Object.keys(first).join(', ')}}` : String(first);
        throw new Error(`Unexpected aggregate result shape: ${shape}. Expected {count: N} or {_id: null, <field>: N}.`);
    }
    if (result && typeof result === 'object' && typeof result.count === 'number') {
        return result.count;
    }
    throw new Error(`Unexpected aggregate result type: ${typeof result}. Expected a number, array, or object with a count field.`);
}
/**
 * Count items in a collection path. Returns a numeric result.
 *
 * This uses the AI query engine with a count-specific prompt prefix,
 * so TaroBase will generate a $count aggregation pipeline and return
 * just the count rather than full documents.
 *
 * IMPORTANT: This only works for collections where the read policy is "true".
 * If the read policy requires per-document checks, the server will return
 * an error because aggregate counts cannot be performed without pulling all
 * documents for access control evaluation.
 *
 * @param path - Collection path (e.g., "posts", "users/abc/comments")
 * @param opts - Optional filter prompt and overrides
 * @returns AggregateResult with the count value
 */
async function count(path, opts = {}) {
    const prefix = 'Return ONLY the total count of matching documents. Use the $count stage to produce a field named "count". Do NOT return the documents themselves.';
    const fullPrompt = opts.prompt
        ? `${prefix} Filter: ${opts.prompt}`
        : prefix;
    const result = await get(path, { prompt: fullPrompt, bypassCache: true, _overrides: opts._overrides });
    return { value: parseAggregateValue(result) };
}
/**
 * Run an aggregate operation on a collection path. Returns a numeric result.
 *
 * Supported operations:
 * - count: Total number of documents
 * - uniqueCount: Number of distinct values for a field
 * - sum: Sum of a numeric field
 * - avg: Average of a numeric field
 * - min: Minimum value of a numeric field
 * - max: Maximum value of a numeric field
 *
 * IMPORTANT: This only works for collections where the read policy is "true".
 * If the read policy requires per-document checks, the server will return
 * an error because aggregate operations cannot be performed without pulling
 * all documents for access control evaluation.
 *
 * @param path - Collection path (e.g., "posts", "users/abc/comments")
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
async function aggregate(path, operation, opts = {}) {
    let prefix;
    switch (operation) {
        case 'count':
            prefix = 'Return ONLY the total count of matching documents. Use the $count stage to produce a field named "count". Do NOT return the documents themselves.';
            break;
        case 'uniqueCount':
            if (!opts.field)
                throw new Error('aggregate "uniqueCount" requires a field option');
            validateFieldName(opts.field);
            prefix = `Return ONLY the count of unique/distinct values of the "${opts.field}" field. Use $group with _id set to "$${opts.field}" then $count to produce a field named "count". Do NOT return the documents themselves.`;
            break;
        case 'sum':
            if (!opts.field)
                throw new Error('aggregate "sum" requires a field option');
            validateFieldName(opts.field);
            prefix = `Return ONLY the sum of the "${opts.field}" field across all matching documents. Use $group with _id: null and result: { $sum: "$${opts.field}" }. Do NOT return the documents themselves.`;
            break;
        case 'avg':
            if (!opts.field)
                throw new Error('aggregate "avg" requires a field option');
            validateFieldName(opts.field);
            prefix = `Return ONLY the average of the "${opts.field}" field across all matching documents. Use $group with _id: null and result: { $avg: "$${opts.field}" }. Do NOT return the documents themselves.`;
            break;
        case 'min':
            if (!opts.field)
                throw new Error('aggregate "min" requires a field option');
            validateFieldName(opts.field);
            prefix = `Return ONLY the minimum value of the "${opts.field}" field across all matching documents. Use $group with _id: null and result: { $min: "$${opts.field}" }. Do NOT return the documents themselves.`;
            break;
        case 'max':
            if (!opts.field)
                throw new Error('aggregate "max" requires a field option');
            validateFieldName(opts.field);
            prefix = `Return ONLY the maximum value of the "${opts.field}" field across all matching documents. Use $group with _id: null and result: { $max: "$${opts.field}" }. Do NOT return the documents themselves.`;
            break;
        default:
            throw new Error(`Unsupported aggregate operation: ${operation}`);
    }
    const fullPrompt = opts.prompt
        ? `${prefix} Filter: ${opts.prompt}`
        : prefix;
    const result = await get(path, { prompt: fullPrompt, bypassCache: true, _overrides: opts._overrides });
    // For uniqueCount, the AI may return $group results without the final $count
    // stage, producing [{_id: "val1"}, {_id: "val2"}, ...]. Verify elements look
    // like $group output (only _id key) before using array length as the count.
    if (operation === 'uniqueCount' && Array.isArray(result) && result.length > 1) {
        const looksLikeGroupOutput = result.every((el) => el && typeof el === 'object' && Object.keys(el).length === 1 && '_id' in el);
        if (looksLikeGroupOutput) {
            return { value: result.length };
        }
        throw new Error(`Unexpected uniqueCount result: got ${result.length} elements that don't match $group output shape.`);
    }
    return { value: parseAggregateValue(result) };
}
/**
 * Cache-scope suffix so cached reads never leak across environments (effective
 * app id) or auth identity. Two reads of the SAME path in different environments
 * within the 500ms TTL — e.g. `get('accessConfig/global', {_overrides:{headers:
 * {'x-app-id': LIVE}}})` then the same for PREVIEW — would otherwise collide and
 * return the first environment's data (the bug that made cross-environment
 * debugging return contradictory reads).
 *
 * The effective routing app id is the `x-app-id` (or `x-public-app-id`) header on
 * `_overrides` — the same header that routes `/items` (see api.ts). Auth identity
 * is the `Authorization` override header, else the wallet-client `_walletAddress`.
 *
 * Kept as a PATH-FIRST suffix so `clearCache` / `clearCacheByPrefix` — which match
 * on the path segment before the first ':' — still invalidate every scoped
 * variant of a path.
 */
function readScopeSuffix(overrides) {
    var _a, _b;
    if (!overrides)
        return '';
    let appId = '';
    let auth = '';
    const headers = overrides.headers;
    if (headers) {
        for (const k of Object.keys(headers)) {
            const lk = k.toLowerCase();
            if (!appId && (lk === 'x-app-id' || lk === 'x-public-app-id'))
                appId = String((_a = headers[k]) !== null && _a !== void 0 ? _a : '');
            else if (!auth && lk === 'authorization')
                auth = String((_b = headers[k]) !== null && _b !== void 0 ? _b : '');
        }
    }
    const authId = auth || overrides._walletAddress || '';
    return `${appId ? `:a${appId}` : ''}${authId ? `:u${hashForKey$1(authId)}` : ''}`;
}
async function get(path, opts = {}) {
    try {
        let normalizedPath = path.startsWith("/") ? path.slice(1) : path;
        // Remove last '*' if it exists in the path
        if (normalizedPath.endsWith("*") && normalizedPath.length > 1) {
            normalizedPath = normalizedPath.slice(0, -1);
        }
        if (!normalizedPath || normalizedPath.length === 0) {
            return new Error("Invalid path provided.");
        }
        // Create cache key combining path, prompt, includeSubPaths, shape, limit, cursor,
        // and the app-id/auth scope (so reads never leak across environments or callers).
        const shapeKey = opts.shape ? JSON.stringify(opts.shape) : '';
        const includeSubPathsKey = opts.includeSubPaths ? ':subpaths' : '';
        const limitKey = opts.limit !== undefined ? `:l${opts.limit}` : '';
        const cursorKey = opts.cursor ? `:c${hashForKey$1(opts.cursor)}` : '';
        const scopeSuffix = readScopeSuffix(opts._overrides);
        const cacheKey = `${normalizedPath}:${opts.prompt || ''}${includeSubPathsKey}:${shapeKey}${limitKey}${cursorKey}${scopeSuffix}`;
        const now = Date.now();
        // Check for valid cache entry if not bypassing cache
        if (!opts.bypassCache && getCache[cacheKey] && now < getCache[cacheKey].expiresAt) {
            return getCache[cacheKey].data;
        }
        // If we're bypassing cache, we should still coalesce identical requests
        // but need a different key to avoid interfering with regular requests
        const requestKey = opts.bypassCache ? `bypass:${cacheKey}` : cacheKey;
        // Check if there's already an in-flight request for this key
        // Fix for TS error - use hasOwnProperty to explicitly check if key exists
        if (Object.prototype.hasOwnProperty.call(pendingRequests, requestKey)) {
            // Return the existing promise to coalesce requests
            return pendingRequests[requestKey];
        }
        // Create a new request promise and store it
        const requestPromise = (async () => {
            try {
                // Cache miss or bypass - proceed with API request
                const pathIsDocument = normalizedPath.split("/").length % 2 === 0;
                let response;
                // Build common query params
                const includeSubPathsParam = opts.includeSubPaths ? '&includeSubPaths=true' : '';
                const shapeParam = opts.shape ? `&shape=${encodeURIComponent(JSON.stringify(opts.shape))}` : '';
                const limitParam = opts.limit !== undefined ? `&limit=${opts.limit}` : '';
                const cursorParam = opts.cursor ? `&cursor=${encodeURIComponent(opts.cursor)}` : '';
                if (pathIsDocument) {
                    const itemId = encodeURIComponent(normalizedPath);
                    // For documents, query params go after the path
                    const queryParams = [includeSubPathsParam, shapeParam].filter(p => p).join('');
                    const apiPath = queryParams ? `items/${itemId}?${queryParams.substring(1)}` : `items/${itemId}`;
                    response = await makeApiRequest('GET', apiPath, null, opts._overrides);
                }
                else {
                    const path = encodeURIComponent(normalizedPath);
                    const promptQueryParam = (opts === null || opts === void 0 ? void 0 : opts.prompt) ? `&prompt=${safeBtoa(opts.prompt)}` : "";
                    const apiPath = `items?path=${path}${promptQueryParam}${includeSubPathsParam}${shapeParam}${limitParam}${cursorParam}`;
                    response = await makeApiRequest('GET', apiPath, null, opts._overrides);
                }
                const responseData = response.data;
                // Cache the response (unless bypassing cache)
                if (!opts.bypassCache) {
                    getCache[cacheKey] = {
                        data: responseData,
                        expiresAt: now + GET_CACHE_TTL
                    };
                    // Periodically clean up expired cache entries (every 5 seconds)
                    if (now - lastCacheCleanup > 5000) {
                        cleanupExpiredCache();
                        lastCacheCleanup = now;
                    }
                }
                // Return the data from the response
                return responseData;
            }
            finally {
                // Remove this request from pendingRequests regardless of success/failure
                delete pendingRequests[requestKey];
            }
        })();
        // Store the promise so other calls can join it
        pendingRequests[requestKey] = requestPromise;
        // Return the promise
        return requestPromise;
    }
    catch (error) {
        throw error;
    }
}
// Helper to clean up expired cache entries
function cleanupExpiredCache() {
    const now = Date.now();
    Object.keys(getCache).forEach(key => {
        if (getCache[key] && getCache[key].expiresAt < now) {
            delete getCache[key];
        }
    });
    lastCacheCleanup = now;
}
async function getMany(paths, opts = {}) {
    if (paths.length === 0) {
        return [];
    }
    // Scope suffix (app id + auth identity) applied to every getMany cache key so
    // batch reads never leak across environments or callers.
    const scopeSuffix = readScopeSuffix(opts._overrides);
    if (paths.length > 30) {
        throw new Error('Cannot fetch more than 30 documents at once');
    }
    const normalizedPaths = [];
    for (const path of paths) {
        let normalizedPath = path.startsWith("/") ? path.slice(1) : path;
        if (normalizedPath.endsWith("*") && normalizedPath.length > 1) {
            normalizedPath = normalizedPath.slice(0, -1);
        }
        if (!normalizedPath || normalizedPath.length === 0) {
            throw new Error(`Invalid path provided: ${path}`);
        }
        const pathIsDocument = normalizedPath.split("/").length % 2 === 0;
        if (!pathIsDocument) {
            throw new Error(`Path must point to a document (even number of segments): ${path}`);
        }
        normalizedPaths.push(normalizedPath);
    }
    const now = Date.now();
    const results = new Array(paths.length);
    const uncachedIndices = [];
    const uncachedPaths = [];
    for (let i = 0; i < normalizedPaths.length; i++) {
        const normalizedPath = normalizedPaths[i];
        const cacheKey = `${normalizedPath}:${scopeSuffix}`;
        if (!opts.bypassCache && getCache[cacheKey] && now < getCache[cacheKey].expiresAt) {
            results[i] = { path: normalizedPath, data: getCache[cacheKey].data };
        }
        else {
            uncachedIndices.push(i);
            uncachedPaths.push(normalizedPath);
        }
    }
    if (uncachedPaths.length > 0) {
        try {
            const response = await makeApiRequest('POST', 'items/batch', { paths: uncachedPaths }, opts._overrides);
            const serverResults = response.data.results;
            const serverResultsMap = new Map();
            for (const result of serverResults) {
                serverResultsMap.set(result.path, result);
            }
            for (let i = 0; i < uncachedIndices.length; i++) {
                const originalIndex = uncachedIndices[i];
                const normalizedPath = uncachedPaths[i];
                const serverResult = serverResultsMap.get(normalizedPath);
                if (serverResult) {
                    results[originalIndex] = serverResult;
                    if (!serverResult.error && !opts.bypassCache) {
                        const cacheKey = `${normalizedPath}:${scopeSuffix}`;
                        getCache[cacheKey] = {
                            data: serverResult.data,
                            expiresAt: now + GET_CACHE_TTL
                        };
                    }
                }
                else {
                    results[originalIndex] = {
                        path: normalizedPath,
                        data: null,
                        error: { code: 'NOT_FOUND', message: `No result returned for path ${normalizedPath}` }
                    };
                }
            }
            if (now - lastCacheCleanup > 5000) {
                cleanupExpiredCache();
                lastCacheCleanup = now;
            }
        }
        catch (error) {
            for (const originalIndex of uncachedIndices) {
                results[originalIndex] = {
                    path: normalizedPaths[originalIndex],
                    data: null,
                    error: { code: 'NOT_FOUND', message: error instanceof Error ? error.message : 'Unknown error' }
                };
            }
        }
    }
    return results;
}
async function runQuery(absolutePath, queryName, queryArgs, opts) {
    const result = await runQueryMany([{ absolutePath, queryName, queryArgs }], opts);
    return result[0];
}
async function runQueryMany(many, opts) {
    try {
        let queries = [];
        for (const { absolutePath, queryName, queryArgs } of many) {
            const normalizedPath = absolutePath.startsWith("/") ? absolutePath.slice(1) : absolutePath;
            const pathIsDocument = normalizedPath.split("/").length % 2 === 0;
            if (!pathIsDocument) {
                throw new Error(`Invalid path provided make sure it is pointing to a document: ${absolutePath}`);
            }
            queries.push({ path: normalizedPath, queryName, queryArgs });
        }
        const response = await makeApiRequest('POST', 'queries', { queries }, opts === null || opts === void 0 ? void 0 : opts._overrides);
        return response.data.queries.map((result) => result.result);
    }
    catch (error) {
        throw error;
    }
}
// NEW: Ad-hoc expression execution - first-class feature
async function runExpression(expression, queryArgs, options) {
    const results = await runExpressionMany([Object.assign({ expression, queryArgs }, options)]);
    return results[0];
}
async function runExpressionMany(many) {
    try {
        let queries = [];
        let overrides = undefined;
        for (const { expression, queryArgs, returnType, _overrides } of many) {
            // Collect overrides from first query that has them (all queries share same request)
            if (_overrides && !overrides) {
                overrides = _overrides;
            }
            queries.push({ queryArgs, expression, returnType });
        }
        const response = await makeApiRequest('POST', 'queries', { queries }, overrides);
        // Map results - trace is automatically included by server for non-mainnet networks
        return response.data.queries.map((queryResult) => {
            const result = { result: queryResult.result };
            // Include trace if present (server includes it for non-mainnet networks)
            if (queryResult.trace) {
                result.trace = queryResult.trace;
            }
            return result;
        });
    }
    catch (error) {
        throw error;
    }
}
async function set(path, document, options) {
    const result = await setMany([{ path, document }], options);
    // Clear cache entries that might be affected by this update
    clearCacheByPrefix(path);
    return result;
}
async function setMany(many, options) {
    var _a, _b, _c, _d, _e, _f, _g;
    // Returns the data that was set, or undefined if the document was already set.
    try {
        const config = await getConfig();
        const authProvider = (_b = (_a = options === null || options === void 0 ? void 0 : options._overrides) === null || _a === void 0 ? void 0 : _a.authProvider) !== null && _b !== void 0 ? _b : config.authProvider;
        if (!authProvider) {
            throw new Error('Auth provider not initialized');
        }
        let documents = [];
        const pathsToInvalidate = new Set();
        for (const { path, document: documentWithReservedFields } of many) {
            // for setting, we should remove any pieces of the document that start with 'tarobase_' as they are internal and shouldn't be updated. The API would block it if it is one of the reserved ones
            let document = documentWithReservedFields;
            if (documentWithReservedFields) {
                document = Object.fromEntries(Object.entries(documentWithReservedFields).filter(([key]) => !key.startsWith('tarobase_')));
            }
            const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
            pathsToInvalidate.add(normalizedPath);
            const pathIsDocument = normalizedPath.split("/").length % 2 === 0;
            let curItemId = "";
            if (pathIsDocument) {
                curItemId = normalizedPath.split("/").pop() ? normalizedPath.split("/").pop() : "";
            }
            else {
                curItemId = Math.random().toString(36).slice(2).substring(0, 12);
            }
            // if the path is a document, we need to append the document id to the path
            const collectionPath = pathIsDocument ? normalizedPath.split("/").slice(0, -1).join("/") : normalizedPath;
            pathsToInvalidate.add(collectionPath);
            let destinationPath = collectionPath;
            if (curItemId && curItemId.length > 0) {
                destinationPath = `${destinationPath}/${curItemId}`;
            }
            documents.push({ destinationPath, document });
        }
        let setResponse;
        try {
            setResponse = await makeApiRequest('PUT', `items`, { documents }, options === null || options === void 0 ? void 0 : options._overrides);
        }
        catch (error) {
            if ((error === null || error === void 0 ? void 0 : error.statusCode) === 402 && (error === null || error === void 0 ? void 0 : error.error) === 'INSUFFICIENT_BALANCE') {
                const deficitLamports = Number((_c = error.deficitLamports) !== null && _c !== void 0 ? _c : 0);
                const deficitSol = Number((_d = error.deficitSol) !== null && _d !== void 0 ? _d : deficitLamports / 1000000000);
                throw new InsufficientBalanceError(String((_e = error.address) !== null && _e !== void 0 ? _e : ''), Number((_f = error.balanceLamports) !== null && _f !== void 0 ? _f : 0), Number((_g = error.estimatedCostLamports) !== null && _g !== void 0 ? _g : 0), deficitLamports, deficitSol);
            }
            throw error;
        }
        // Invalidate caches and pending requests for affected paths
        pathsToInvalidate.forEach(path => {
            clearCacheByPrefix(path);
        });
        if (setResponse.status === 202) {
            // This means that the document needs to be set on-chain (or signed for offchain),
            // the API did nothing and gave us the required info to run this transaction.
            const { transactions, offchainTransaction } = setResponse.data;
            // Handle offchain transaction flow (for 'offchain' protocol apps)
            if (offchainTransaction) {
                const transactionResult = await handleOffchainTransaction(offchainTransaction, authProvider, options);
                return Object.assign(Object.assign({}, documents.map(d => d.document)), { transactionId: transactionResult.signature, signedTransaction: transactionResult.signedTransaction });
            }
            // Handle Solana on-chain transaction flow
            let lastTxSignature = undefined;
            let signedTransaction = undefined;
            for (let i = 0; i < transactions.length; i++) {
                const curTx = transactions[i];
                const transactionResult = await handleSolanaTransaction(curTx, authProvider, options);
                lastTxSignature = transactionResult.transactionSignature;
                signedTransaction = transactionResult.signedTransaction;
            }
            // Sync items after all transactions are confirmed
            // Wait for 1.5 seconds to ensure all transactions are confirmed
            await new Promise(resolve => setTimeout(resolve, 1500));
            await syncItems(many.map(m => m.path), options);
            // TODO: Should we wait here or do the optimistic subscription updates like below?
            return Object.assign(Object.assign({}, documents.map(d => d.document)), { transactionId: lastTxSignature, signedTransaction: signedTransaction });
        }
        else if (setResponse.status === 200) {
            // This means that the document was set successfully.
            // TODO: If we're able to be smarter about it, we can return the document here, and get it back from the server
            // rather than just using what we set.
            // TODO: We can also trigger subscriptions here maybe? Can even recall all relevant subscriptions here.
            if (setResponse.data === true) {
                return Object.assign(Object.assign({}, documents.map(d => d.document)), { transactionId: null });
            }
            else if (setResponse.data &&
                typeof setResponse.data === 'object' &&
                setResponse.data.success === true) {
                const _h = setResponse.data, { success: _success } = _h, rest = __rest(_h, ["success"]);
                return Object.assign(Object.assign(Object.assign({}, documents.map(d => d.document)), rest), { transactionId: null });
            }
            else {
                return Object.assign(Object.assign({}, setResponse.data), { transactionId: null });
            }
        }
        else {
            throw new Error(`Failed to set document: ${setResponse}`);
        }
    }
    catch (error) {
        throw error;
    }
    async function handleSolanaTransaction(tx, authProvider, options) {
        var _a, _b, _c, _d, _e;
        const solTransactionData = {
            remainingAccounts: tx.remainingAccounts,
            setDocumentData: tx.transactionArgs.setDocumentData,
            deletePaths: tx.transactionArgs.deletePaths,
            idl: tx.idl,
            txData: (_b = (_a = tx.txData) === null || _a === void 0 ? void 0 : _a.map((txData) => ({
                pluginFunctionKey: txData.pluginFunctionKey,
                txData: bufferExports.Buffer.from(txData.txData),
                // raIndices is Vec<u8> on-chain (serialized as Buffer/bytes). The server may send
                // it as a Buffer, a number[], a Uint8Array, or — over JSON — as Node's serialized
                // Buffer shape {type: "Buffer", data: number[]} (the default `JSON.stringify(buf)`
                // output). Normalise all of those to Buffer here. Each value must fit in u8 (matches
                // Solana's instruction-level account indexing); throw clearly if anything is out of range.
                raIndices: (() => {
                    const raw = txData.raIndices;
                    if (raw == null)
                        return bufferExports.Buffer.alloc(0);
                    if (bufferExports.Buffer.isBuffer(raw))
                        return raw;
                    if (raw instanceof Uint8Array)
                        return bufferExports.Buffer.from(raw);
                    // Node's JSON-serialised Buffer: {type: "Buffer", data: number[]}
                    const arrayLike = Array.isArray(raw)
                        ? raw
                        : (raw && typeof raw === 'object' && Array.isArray(raw.data))
                            ? raw.data
                            : (() => { throw new Error(`raIndices has unexpected shape: ${typeof raw}`); })();
                    const bytes = arrayLike.map((raIndex) => {
                        const n = isHexString(raIndex) ? parseInt(raIndex, 16) : Number(raIndex);
                        if (!Number.isInteger(n) || n < 0 || n > 255) {
                            throw new Error(`raIndex ${raIndex} is not a valid u8 (must be integer in 0..255)`);
                        }
                        return n;
                    });
                    return bufferExports.Buffer.from(bytes);
                })(),
            }))) !== null && _b !== void 0 ? _b : [],
        };
        const config = await getConfig();
        const solTransaction = {
            appId: config.appId,
            txArgs: [solTransactionData],
            lutKey: (_c = tx.lutAddress) !== null && _c !== void 0 ? _c : null,
            additionalLutAddresses: tx.additionalLutAddresses,
            network: tx.network,
            preInstructions: (_e = (_d = tx.preInstructions) === null || _d === void 0 ? void 0 : _d.map((ix) => {
                var _a;
                const keys = (_a = ix.keys) === null || _a === void 0 ? void 0 : _a.map((k) => ({
                    pubkey: new PublicKey(k.pubkey),
                    isSigner: k.isSigner,
                    isWritable: k.isWritable,
                }));
                const programId = ix.programId === SystemProgram.programId.toBase58()
                    ? SystemProgram.programId // prettier to use the constant
                    : new PublicKey(ix.programId);
                const data = bufferExports.Buffer.from(ix.data);
                return new TransactionInstruction({
                    keys,
                    programId,
                    data,
                });
            })) !== null && _e !== void 0 ? _e : [],
            // Server co-signed transaction (when CPI tx_data is present)
            signedTransaction: tx.signedTransaction,
        };
        const transactionResult = await authProvider.runTransaction(undefined, solTransaction, options);
        return transactionResult;
    }
    async function handleOffchainTransaction(tx, authProvider, options) {
        var _a, _b, _c, _d, _e, _f;
        const config = await getConfig();
        // 1. Sign the transaction message using mock signing for offchain transactions
        // Use signMessageMock if available (OffchainAuthProvider), otherwise fall back to signMessage
        const signature = authProvider.signMessageMock
            ? await authProvider.signMessageMock(tx.message)
            : await authProvider.signMessage(tx.message);
        // 2. Create signed transaction
        const signedTx = {
            transaction: tx,
            signature
        };
        // 3. If shouldSubmitTx is false, return signed but not submitted
        if ((options === null || options === void 0 ? void 0 : options.shouldSubmitTx) === false) {
            return {
                signature: '',
                signedTransaction: bufferExports.Buffer.from(JSON.stringify(signedTx)).toString('base64')
            };
        }
        // 4. Submit to RPC endpoint via makeApiRequest (gets auth headers + 401 retry)
        const appId = ((_b = (_a = options === null || options === void 0 ? void 0 : options._overrides) === null || _a === void 0 ? void 0 : _a.headers) === null || _b === void 0 ? void 0 : _b['x-app-id']) || ((_d = (_c = options === null || options === void 0 ? void 0 : options._overrides) === null || _c === void 0 ? void 0 : _c.headers) === null || _d === void 0 ? void 0 : _d['X-App-Id']) || config.appId;
        const rpcResponse = await makeApiRequest('POST', `/app/${appId}/rpc`, {
            jsonrpc: '2.0',
            id: 1,
            method: 'sendTransaction',
            params: [bufferExports.Buffer.from(JSON.stringify(signedTx)).toString('base64')]
        }, options === null || options === void 0 ? void 0 : options._overrides);
        if ((_e = rpcResponse.data) === null || _e === void 0 ? void 0 : _e.error) {
            throw new Error(rpcResponse.data.error.message);
        }
        return { signature: (_f = rpcResponse.data) === null || _f === void 0 ? void 0 : _f.result };
    }
}
// Helper to clear cache entries by prefix
function clearCacheByPrefix(prefix) {
    // Fixed: Use prefix instead of undefined 'path' variable
    const normalizedPrefix = prefix.startsWith("/") ? prefix.slice(1) : prefix;
    // Clear cache entries
    Object.keys(getCache).forEach(key => {
        const [path] = key.split(':');
        if (path === normalizedPrefix || path.startsWith(`${normalizedPrefix}/`)) {
            delete getCache[key];
        }
    });
    // Cancel pending requests for this path
    Object.keys(pendingRequests).forEach(key => {
        // Skip bypass-specific requests which have a different format
        if (key.startsWith('bypass:'))
            return;
        const [path] = key.split(':');
        if (path === normalizedPrefix || path.startsWith(`${normalizedPrefix}/`)) {
            // We don't actually abort the fetch (we could with AbortController),
            // but we remove it from pending so new requests won't coalesce with it
            delete pendingRequests[key];
        }
    });
}
async function getFiles(path, options) {
    try {
        const normalizedPath = path.startsWith("/") ? path.slice(1) : path;
        if (!normalizedPath || normalizedPath.length === 0) {
            return new Error("Invalid path provided.");
        }
        const apiPath = `storage?path=${normalizedPath}`;
        const response = await makeApiRequest('GET', apiPath, null, options === null || options === void 0 ? void 0 : options._overrides);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}
async function setFile(path, file, options) {
    var _a;
    // 1) Get the presigned URL from your backend
    const requestBody = {
        destinationPath: path,
        operation: file ? 'upload' : 'delete',
    };
    // Send file size so the server can enforce upload limits before issuing a presigned URL
    if (file) {
        requestBody.contentLength = file.size;
    }
    const response = await makeApiRequest('POST', 'storage/url', requestBody, options === null || options === void 0 ? void 0 : options._overrides);
    if (file == null) {
        return true;
    }
    if (!((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.s3Url)) {
        throw new Error("No s3Url in response");
    }
    // Client-side size guard using the limit returned by the server
    const maxFileSize = response.data.maxFileSize;
    if (maxFileSize && file.size > maxFileSize) {
        throw new Error(`File size ${(file.size / (1024 * 1024)).toFixed(2)} MB exceeds the maximum upload size of ${(maxFileSize / (1024 * 1024)).toFixed(0)} MB.`);
    }
    const presignedUrl = response.data.s3Url;
    // 2) Upload the file directly to S3 with a PUT request
    const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
            'Content-Type': file.type,
        },
        body: file
    });
    // 3) Check if the upload was successful
    if (!uploadResponse.ok) {
        await uploadResponse.text();
        throw new Error(`Upload failed with status: ${uploadResponse.status}`);
    }
    return true;
}
async function signMessage(message) {
    const config = await getConfig();
    if (!config.authProvider) {
        throw new Error('Auth provider not initialized. Please call init() first.');
    }
    return config.authProvider.signMessage(message);
}
async function signTransaction(transaction) {
    const config = await getConfig();
    if (!config.authProvider) {
        throw new Error('Auth provider not initialized. Please call init() first.');
    }
    return config.authProvider.signTransaction(transaction);
}
async function signAndSubmitTransaction(transaction, feePayer) {
    const config = await getConfig();
    if (!config.authProvider) {
        throw new Error('Auth provider not initialized. Please call init() first.');
    }
    return config.authProvider.signAndSubmitTransaction(transaction, feePayer);
}
async function syncItems(paths, options) {
    try {
        const response = await makeApiRequest('PUT', 'items/sync', { paths }, options === null || options === void 0 ? void 0 : options._overrides);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

/**
 * WebSocket v2 Subscription Manager
 *
 * This module implements multiplexed subscriptions over a single WebSocket connection.
 * It maintains the same external API as v1 but uses the new v2 protocol internally.
 */
// ============ Global State ============
// One connection per appId
const connections = new Map();
const responseCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const TOKEN_REFRESH_BUFFER = 10 * 60 * 1000; // Refresh token 10 minutes before expiry
const TOKEN_CHECK_INTERVAL = 5 * 60 * 1000; // Check token freshness every 5 minutes
// ============ WebSocket Config ============
const BASE_MIN_RECONNECT_DELAY_MS = 1000;
const MIN_RECONNECT_DELAY_JITTER_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 300000;
const RECONNECT_DELAY_GROW_FACTOR = 1.8;
const MIN_BROWSER_RECONNECT_INTERVAL_MS = 5000;
const MAX_AUTH_REFRESH_RETRIES = 5;
const WS_CONFIG = {
    // Keep retrying indefinitely so long outages recover without page refresh.
    maxRetries: Infinity,
    // Conservative starting delay with jitter spreads reconnects across clients.
    minReconnectionDelay: BASE_MIN_RECONNECT_DELAY_MS + Math.floor(Math.random() * MIN_RECONNECT_DELAY_JITTER_MS),
    // Stretch backoff to 5 minutes during prolonged outages to protect backend pools.
    maxReconnectionDelay: MAX_RECONNECT_DELAY_MS,
    // Moderate growth to avoid rapid retry bursts while still converging to max delay.
    reconnectionDelayGrowFactor: RECONNECT_DELAY_GROW_FACTOR,
    // Give handshakes more time before considering the attempt failed.
    connectionTimeout: 10000,
};
const WS_V2_PATH = '/ws/v2';
let browserReconnectHooksAttached = false;
let lastBrowserTriggeredReconnectAt = 0;
let reconnectInProgress = null;
// ============ Helper Functions ============
function generateSubscriptionId() {
    return `sub_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}
function hashForKey(value) {
    let h = 5381;
    for (let i = 0; i < value.length; i++) {
        h = ((h << 5) + h + value.charCodeAt(i)) & 0x7fffffff;
    }
    return h.toString(36);
}
function getCacheKey(path, prompt, shape, limit, cursor) {
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    const shapeKey = shape && Object.keys(shape).length > 0 ? JSON.stringify(shape) : '';
    const limitKey = limit !== undefined ? `:l${limit}` : '';
    const cursorKey = cursor ? `:c${hashForKey(cursor)}` : '';
    return `${normalizedPath}:${prompt || 'default'}:${shapeKey}${limitKey}${cursorKey}`;
}
function isTokenExpired(token) {
    try {
        const payload = JSON.parse(decodeBase64Url(token.split('.')[1]));
        const expirationTime = payload.exp * 1000;
        const currentTime = Date.now();
        return currentTime > (expirationTime - 60000); // 60 second buffer
    }
    catch (error) {
        console.error('[WS v2] Error checking token expiration:', error);
        return true;
    }
}
function getTokenExpirationTime(token) {
    try {
        const payload = JSON.parse(decodeBase64Url(token.split('.')[1]));
        return payload.exp ? payload.exp * 1000 : null;
    }
    catch (_a) {
        return null;
    }
}
function scheduleTokenRefresh(connection, isServer) {
    // Clear any existing timer
    if (connection.tokenRefreshTimer) {
        clearInterval(connection.tokenRefreshTimer);
        connection.tokenRefreshTimer = null;
    }
    // Periodic check: every TOKEN_CHECK_INTERVAL, check if the token needs refreshing.
    // This replaces the old single setTimeout approach which was unreliable for long
    // delays (browsers throttle/suspend timers in background tabs).
    connection.tokenRefreshTimer = setInterval(async () => {
        var _a, _b;
        try {
            const currentToken = await getIdToken(isServer);
            if (!currentToken)
                return; // Unauthenticated, nothing to refresh
            const expirationTime = getTokenExpirationTime(currentToken);
            if (!expirationTime)
                return;
            const timeUntilExpiry = expirationTime - Date.now();
            if (timeUntilExpiry <= TOKEN_REFRESH_BUFFER) {
                console.info('[WS v2] Token expiring soon, proactively refreshing');
                // Refresh the token and store it. Do NOT reconnect — the server only
                // verifies JWT at $connect time, so the existing connection continues
                // working. The fresh token is picked up by urlProvider the next time
                // a reconnect naturally happens (network drop, keepalive timeout, etc.).
                try {
                    const currentRefreshToken = await getRefreshToken(isServer);
                    if (!currentRefreshToken) {
                        console.warn('[WS v2] No refresh token available for proactive refresh');
                        return;
                    }
                    const refreshData = await refreshSession(currentRefreshToken);
                    if (refreshData && refreshData.idToken && refreshData.accessToken) {
                        await updateIdTokenAndAccessToken(refreshData.idToken, refreshData.accessToken, isServer);
                        console.info('[WS v2] Token refreshed successfully, stored for next connection');
                    }
                    else {
                        console.warn('[WS v2] Proactive token refresh returned incomplete data');
                    }
                }
                catch (refreshError) {
                    // If the refresh token itself is invalid (401/403), stop retrying —
                    // the token won't magically become valid next interval, and continuing
                    // to hammer /session/refresh causes 429 rate-limit storms.
                    if (((_a = refreshError === null || refreshError === void 0 ? void 0 : refreshError.response) === null || _a === void 0 ? void 0 : _a.status) === 401 || ((_b = refreshError === null || refreshError === void 0 ? void 0 : refreshError.response) === null || _b === void 0 ? void 0 : _b.status) === 403) {
                        console.warn('[WS v2] Refresh token rejected (401/403), stopping periodic refresh');
                        if (connection.tokenRefreshTimer) {
                            clearInterval(connection.tokenRefreshTimer);
                            connection.tokenRefreshTimer = null;
                        }
                        return;
                    }
                    console.warn('[WS v2] Proactive token refresh failed, will retry next interval:', refreshError);
                }
            }
        }
        catch (error) {
            console.error('[WS v2] Error in periodic token check:', error);
        }
    }, TOKEN_CHECK_INTERVAL);
}
async function getFreshAuthToken(isServer) {
    var _a, _b;
    const currentToken = await getIdToken(isServer);
    if (!currentToken) {
        return null;
    }
    if (!isTokenExpired(currentToken)) {
        return currentToken;
    }
    // Token is expired — attempt refresh
    try {
        const refreshToken = await getRefreshToken(isServer);
        if (!refreshToken) {
            console.warn('[WS v2] Token expired but no refresh token available');
            return null;
        }
        const refreshData = await refreshSession(refreshToken);
        if (refreshData && refreshData.idToken && refreshData.accessToken) {
            await updateIdTokenAndAccessToken(refreshData.idToken, refreshData.accessToken, isServer);
            return refreshData.idToken;
        }
    }
    catch (error) {
        console.error('[WS v2] Error refreshing token:', error);
        // If the refresh token is permanently invalid (401/403), clear the stale
        // session from storage so future attempts don't keep retrying with it.
        if (!isServer && (((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) === 401 || ((_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.status) === 403)) {
            try {
                const { WebSessionManager } = await Promise.resolve().then(function () { return webSessionManager; });
                WebSessionManager.clearSession();
            }
            catch (clearError) {
                console.warn('[WS v2] Failed to clear stale session:', clearError);
            }
        }
    }
    // Return null instead of the expired token to prevent infinite 401 reconnect storms.
    // The server accepts unauthenticated connections; auth-required subscriptions will
    // receive per-subscription errors via onError callbacks.
    console.warn('[WS v2] Token refresh failed, connecting without auth to prevent reconnect storm');
    return null;
}
function hasDisconnectedActiveConnections() {
    for (const connection of connections.values()) {
        if (!connection.ws) {
            continue;
        }
        // Only nudge reconnection when socket is fully closed.
        // If ReconnectingWebSocket is already in CONNECTING/backoff state, don't interfere.
        if (connection.ws.readyState === WS_READY_STATE_CLOSED) {
            return true;
        }
    }
    return false;
}
function maybeReconnectFromBrowserSignal() {
    const now = Date.now();
    if (now - lastBrowserTriggeredReconnectAt < MIN_BROWSER_RECONNECT_INTERVAL_MS) {
        return;
    }
    if (!hasDisconnectedActiveConnections()) {
        return;
    }
    lastBrowserTriggeredReconnectAt = now;
    reconnectWithNewAuthV2().catch((error) => {
        console.error('[WS v2] Browser-triggered reconnect failed:', error);
    });
}
function attachBrowserReconnectHooksOnce() {
    if (browserReconnectHooksAttached) {
        return;
    }
    if (typeof window === 'undefined') {
        return;
    }
    browserReconnectHooksAttached = true;
    window.addEventListener('online', () => {
        maybeReconnectFromBrowserSignal();
    });
    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                maybeReconnectFromBrowserSignal();
            }
        });
    }
}
// ============ Connection Management ============
async function getOrCreateConnection(appId, isServer) {
    attachBrowserReconnectHooksOnce();
    let connection = connections.get(appId);
    if (connection && connection.ws) {
        return connection;
    }
    // Create new connection
    connection = {
        ws: null,
        subscriptions: new Map(),
        pendingSubscriptions: new Map(),
        pendingUnsubscriptions: new Map(),
        isConnecting: false,
        isConnected: false,
        appId,
        tokenRefreshTimer: null,
        consecutiveAuthFailures: 0,
    };
    connections.set(appId, connection);
    // URL provider for reconnection with fresh tokens
    const urlProvider = async () => {
        const config = await getConfig();
        const wsUrl = new URL(config.wsApiUrl);
        // Always use v2 path
        wsUrl.pathname = WS_V2_PATH;
        // Set appId — prefer the explicit appId passed to getOrCreateConnection,
        // fall back to window global for legacy callers, then config default
        if (appId && appId !== config.appId) {
            wsUrl.searchParams.append('appId', appId);
        }
        else if (typeof window !== 'undefined' && window.CUSTOM_TAROBASE_APP_ID_HEADER) {
            wsUrl.searchParams.append('appId', window.CUSTOM_TAROBASE_APP_ID_HEADER);
        }
        else {
            wsUrl.searchParams.append('appId', config.appId);
        }
        // Add auth token if available
        const authToken = await getFreshAuthToken(isServer);
        if (authToken) {
            wsUrl.searchParams.append('authorization', authToken);
            // Successful token acquisition — reset failure counter
            connection.consecutiveAuthFailures = 0;
        }
        else {
            // Check if user WAS authenticated (had a token that expired).
            // If so, retry with exponential backoff before falling back to unauthenticated.
            const expiredToken = await getIdToken(isServer);
            if (expiredToken && isTokenExpired(expiredToken)) {
                connection.consecutiveAuthFailures++;
                if (connection.consecutiveAuthFailures <= MAX_AUTH_REFRESH_RETRIES) {
                    console.warn(`[WS v2] Auth refresh failed (attempt ${connection.consecutiveAuthFailures}/${MAX_AUTH_REFRESH_RETRIES}), retrying with backoff`);
                    throw new Error('Auth token refresh failed, retrying with backoff');
                }
                console.warn('[WS v2] Auth refresh retries exhausted, falling back to unauthenticated connection');
            }
            // No token at all (never authenticated) or retries exhausted — connect without auth
        }
        return wsUrl.toString();
    };
    // Create WebSocket connection
    connection.isConnecting = true;
    const ws = new ReconnectingWebSocket(urlProvider, [], Object.assign(Object.assign({}, WS_CONFIG), { debug: false }));
    connection.ws = ws;
    // Handle connection open
    ws.addEventListener('open', () => {
        connection.isConnecting = false;
        connection.isConnected = true;
        // NOTE: Do NOT reset consecutiveAuthFailures here. It is reset when a
        // fresh auth token is actually obtained (in urlProvider, line ~389) or on
        // an explicit auth change (reconnectWithNewAuthV2, line ~854). Resetting
        // on every 'open' event created an infinite loop: auth fails 5x → connect
        // without auth → open resets counter → disconnect → auth fails 5x again →
        // repeat forever, hammering /session/refresh and causing 429s.
        //
        // An elevated counter is safe for anonymous/guest sessions: when there's no
        // token at all (getIdToken returns null), the counter is never checked —
        // urlProvider skips straight to unauthenticated connection.
        // Schedule periodic token freshness checks
        scheduleTokenRefresh(connection, isServer);
        // Re-subscribe to all existing subscriptions after reconnect
        for (const sub of connection.subscriptions.values()) {
            sub.lastData = undefined;
            sendSubscribe(connection, sub);
        }
    });
    // Handle incoming messages
    ws.addEventListener('message', (event) => {
        try {
            const message = JSON.parse(event.data);
            handleServerMessage(connection, message);
        }
        catch (error) {
            console.error('[WS v2] Error parsing message:', error);
        }
    });
    // Handle errors
    ws.addEventListener('error', (event) => {
        console.error('[WS v2] WebSocket error:', event);
        for (const [, pending] of connection.pendingSubscriptions) {
            pending.reject(new Error('WebSocket error'));
        }
        connection.pendingSubscriptions.clear();
    });
    // Handle close
    ws.addEventListener('close', () => {
        connection.isConnected = false;
        if (connection.tokenRefreshTimer) {
            clearInterval(connection.tokenRefreshTimer);
            connection.tokenRefreshTimer = null;
        }
    });
    return connection;
}
function handleServerMessage(connection, message) {
    var _a;
    switch (message.type) {
        case 'subscribed': {
            const subscription = connection.subscriptions.get(message.subscriptionId);
            if (subscription) {
                // A data update can race ahead of subscribed during reconnect.
                // If we already received data for this subscription, treat subscribed
                // as an ack only and avoid regressing to an older snapshot.
                if (subscription.lastData === undefined) {
                    const cacheKey = getCacheKey(subscription.path, subscription.prompt, subscription.shape, subscription.limit, subscription.cursor);
                    responseCache.set(cacheKey, { data: message.data, timestamp: Date.now() });
                    subscription.lastData = message.data;
                    notifyCallbacks(subscription, message.data);
                }
            }
            // Resolve pending subscription promise
            const pending = connection.pendingSubscriptions.get(message.subscriptionId);
            if (pending) {
                pending.resolve();
                connection.pendingSubscriptions.delete(message.subscriptionId);
            }
            break;
        }
        case 'unsubscribed': {
            // Resolve pending unsubscription promise
            const pending = connection.pendingUnsubscriptions.get(message.subscriptionId);
            if (pending) {
                pending.resolve();
                connection.pendingUnsubscriptions.delete(message.subscriptionId);
            }
            break;
        }
        case 'data': {
            const subscription = connection.subscriptions.get(message.subscriptionId);
            if (subscription) {
                // Update cache
                const cacheKey = getCacheKey(subscription.path, subscription.prompt, subscription.shape, subscription.limit, subscription.cursor);
                responseCache.set(cacheKey, { data: message.data, timestamp: Date.now() });
                // Store last data
                subscription.lastData = message.data;
                // Notify callbacks
                notifyCallbacks(subscription, message.data);
            }
            break;
        }
        case 'error': {
            console.error('[WS v2] Server error:', message.code, message.message);
            if (message.subscriptionId) {
                // Reject pending subscription if this is a subscription error
                const pending = connection.pendingSubscriptions.get(message.subscriptionId);
                if (pending) {
                    pending.reject(new Error(`${message.code}: ${message.message}`));
                    connection.pendingSubscriptions.delete(message.subscriptionId);
                }
                // Notify error callbacks for this subscription
                const subscription = connection.subscriptions.get(message.subscriptionId);
                if (subscription) {
                    for (const callback of subscription.callbacks) {
                        (_a = callback.onError) === null || _a === void 0 ? void 0 : _a.call(callback, new Error(`${message.code}: ${message.message}`));
                    }
                }
            }
            break;
        }
    }
}
function notifyCallbacks(subscription, data) {
    var _a;
    // Snapshot the callbacks array so that unsubscriptions during
    // notification don't cause callbacks to be skipped.
    const callbacks = subscription.callbacks.slice();
    for (const callback of callbacks) {
        try {
            (_a = callback.onData) === null || _a === void 0 ? void 0 : _a.call(callback, data);
        }
        catch (error) {
            console.error('[WS v2] Error in subscription callback:', error);
        }
    }
}
// WebSocket readyState constants
const WS_READY_STATE_OPEN = 1;
const WS_READY_STATE_CLOSED = 3;
function sendSubscribe(connection, subscription) {
    if (!connection.ws || connection.ws.readyState !== WS_READY_STATE_OPEN) {
        return;
    }
    const message = {
        type: 'subscribe',
        subscriptionId: subscription.subscriptionId,
        path: subscription.path,
        prompt: subscription.prompt ? safeBtoa(subscription.prompt) : undefined,
        includeSubPaths: subscription.includeSubPaths,
        shape: subscription.shape && Object.keys(subscription.shape).length > 0
            ? subscription.shape
            : undefined,
        limit: subscription.limit,
        cursor: subscription.cursor,
    };
    try {
        connection.ws.send(JSON.stringify(message));
    }
    catch (error) {
        console.error('[WS v2] Error sending subscribe message:', error);
    }
}
function sendUnsubscribe(connection, subscriptionId) {
    if (!connection.ws || connection.ws.readyState !== WS_READY_STATE_OPEN) {
        return;
    }
    const message = {
        type: 'unsubscribe',
        subscriptionId,
    };
    try {
        connection.ws.send(JSON.stringify(message));
    }
    catch (error) {
        console.error('[WS v2] Error sending unsubscribe message:', error);
    }
}
// ============ Public API ============
/**
 * Subscribe to data at a path using WebSocket v2.
 * Returns an unsubscribe function.
 */
async function subscribeV2(path, subscriptionOptions) {
    const config = await getConfig();
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
    const cacheKey = getCacheKey(normalizedPath, subscriptionOptions.prompt, subscriptionOptions.shape, subscriptionOptions.limit, subscriptionOptions.cursor);
    // Deliver cached data immediately if available
    const cachedEntry = responseCache.get(cacheKey);
    if (cachedEntry && Date.now() - cachedEntry.timestamp < CACHE_TTL && subscriptionOptions.onData) {
        setTimeout(() => {
            var _a;
            (_a = subscriptionOptions.onData) === null || _a === void 0 ? void 0 : _a.call(subscriptionOptions, cachedEntry.data);
        }, 0);
    }
    // Use explicit appId override if provided, otherwise fall back to config
    const effectiveAppId = subscriptionOptions.appId || config.appId;
    // Get or create connection for this appId
    const connection = await getOrCreateConnection(effectiveAppId, config.isServer);
    // Check if we already have a subscription for this path+prompt+shape+limit+cursor
    const shapeKey = subscriptionOptions.shape ? JSON.stringify(subscriptionOptions.shape) : '';
    let existingSubscription;
    for (const sub of connection.subscriptions.values()) {
        const subShapeKey = sub.shape ? JSON.stringify(sub.shape) : '';
        if (sub.path === normalizedPath && sub.prompt === subscriptionOptions.prompt && subShapeKey === shapeKey && sub.limit === subscriptionOptions.limit && sub.cursor === subscriptionOptions.cursor) {
            existingSubscription = sub;
            break;
        }
    }
    if (existingSubscription) {
        // Add callback to existing subscription
        existingSubscription.callbacks.push(subscriptionOptions);
        // Deliver last known data immediately
        if (existingSubscription.lastData !== undefined && subscriptionOptions.onData) {
            setTimeout(() => {
                var _a;
                (_a = subscriptionOptions.onData) === null || _a === void 0 ? void 0 : _a.call(subscriptionOptions, existingSubscription.lastData);
            }, 0);
        }
        return async () => {
            await removeCallbackFromSubscription(connection, existingSubscription.subscriptionId, subscriptionOptions);
        };
    }
    // Create new subscription
    const subscriptionId = generateSubscriptionId();
    const subscription = {
        subscriptionId,
        path: normalizedPath,
        prompt: subscriptionOptions.prompt,
        shape: subscriptionOptions.shape,
        limit: subscriptionOptions.limit,
        cursor: subscriptionOptions.cursor,
        includeSubPaths: false,
        callbacks: [subscriptionOptions],
        lastData: undefined,
    };
    connection.subscriptions.set(subscriptionId, subscription);
    // Send subscribe message if connected
    if (connection.isConnected) {
        // Create a promise to wait for subscription confirmation
        const subscriptionPromise = new Promise((resolve, reject) => {
            connection.pendingSubscriptions.set(subscriptionId, { resolve, reject });
            // Timeout after 10 seconds
            setTimeout(() => {
                if (connection.pendingSubscriptions.has(subscriptionId)) {
                    connection.pendingSubscriptions.delete(subscriptionId);
                    reject(new Error('Subscription timeout'));
                }
            }, 10000);
        });
        sendSubscribe(connection, subscription);
        try {
            await subscriptionPromise;
        }
        catch (error) {
            console.warn('[WS v2] Subscription confirmation failed, keeping for reconnect recovery:', error);
        }
    }
    // Return unsubscribe function
    return async () => {
        await removeCallbackFromSubscription(connection, subscriptionId, subscriptionOptions);
    };
}
async function removeCallbackFromSubscription(connection, subscriptionId, callback) {
    const subscription = connection.subscriptions.get(subscriptionId);
    if (!subscription) {
        return;
    }
    // Remove this callback
    subscription.callbacks = subscription.callbacks.filter(cb => cb !== callback);
    // If there are still callbacks, don't unsubscribe
    if (subscription.callbacks.length > 0) {
        return;
    }
    // No more callbacks, unsubscribe from server
    connection.subscriptions.delete(subscriptionId);
    if (connection.isConnected) {
        // Create a promise to wait for unsubscription confirmation
        const unsubscribePromise = new Promise((resolve, reject) => {
            connection.pendingUnsubscriptions.set(subscriptionId, { resolve, reject });
            // Timeout after 5 seconds
            setTimeout(() => {
                if (connection.pendingUnsubscriptions.has(subscriptionId)) {
                    connection.pendingUnsubscriptions.delete(subscriptionId);
                    resolve(); // Don't throw on unsubscribe timeout
                }
            }, 5000);
        });
        sendUnsubscribe(connection, subscriptionId);
        await unsubscribePromise;
    }
    // If no more subscriptions, close connection
    if (connection.subscriptions.size === 0 && connection.ws) {
        // Clear token refresh timer
        if (connection.tokenRefreshTimer) {
            clearInterval(connection.tokenRefreshTimer);
            connection.tokenRefreshTimer = null;
        }
        connection.ws.close();
        connection.ws = null;
        connections.delete(connection.appId);
    }
}
/**
 * Close all v2 subscriptions.
 */
async function closeAllSubscriptionsV2() {
    const closePromises = [];
    for (const [appId, connection] of connections) {
        if (connection.tokenRefreshTimer) {
            clearInterval(connection.tokenRefreshTimer);
            connection.tokenRefreshTimer = null;
        }
        if (connection.ws) {
            const ws = connection.ws;
            connection.ws = null;
            connection.isConnected = false;
            closePromises.push(new Promise((resolve) => {
                ws.addEventListener('close', () => {
                    resolve();
                });
                ws.close();
            }));
        }
        connections.delete(appId);
    }
    await Promise.all(closePromises);
}
/**
 * Clear the v2 cache.
 */
function clearCacheV2(path) {
    if (path) {
        const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
        // Clear all cache entries that start with this path
        for (const key of responseCache.keys()) {
            if (key.startsWith(normalizedPath)) {
                responseCache.delete(key);
            }
        }
    }
    else {
        responseCache.clear();
    }
}
/**
 * Get cached data for a path (v2).
 */
function getCachedDataV2(path, prompt) {
    const cacheKey = getCacheKey(path, prompt);
    const cachedEntry = responseCache.get(cacheKey);
    if (cachedEntry && Date.now() - cachedEntry.timestamp < CACHE_TTL) {
        return cachedEntry.data;
    }
    return null;
}
/**
 * Reconnect all v2 WebSocket connections with fresh authentication.
 * Call this when the user's auth state changes (login/logout).
 *
 * This will:
 * 1. Close existing connections
 * 2. Re-establish with new auth token
 * 3. Re-subscribe to all active paths
 *
 * Note: Existing subscriptions will receive new initial data after reconnection.
 */
async function reconnectWithNewAuthV2() {
    if (reconnectInProgress) {
        return reconnectInProgress;
    }
    reconnectInProgress = doReconnectWithNewAuth();
    try {
        await reconnectInProgress;
    }
    finally {
        reconnectInProgress = null;
    }
}
async function doReconnectWithNewAuth() {
    for (const [appId, connection] of connections) {
        if (!connection.ws) {
            continue;
        }
        for (const [, pending] of connection.pendingSubscriptions) {
            pending.reject(new Error('Connection reconnecting due to auth change'));
        }
        connection.pendingSubscriptions.clear();
        for (const [, pending] of connection.pendingUnsubscriptions) {
            pending.resolve();
        }
        connection.pendingUnsubscriptions.clear();
        // Reset auth failure counter — this is a proactive reconnect (login, token refresh)
        connection.consecutiveAuthFailures = 0;
        // Close the WebSocket (this triggers reconnection in ReconnectingWebSocket)
        // We use reconnect() which will close and re-open with fresh URL (including new token)
        try {
            connection.ws.reconnect();
        }
        catch (error) {
            console.error('[WS v2] Error reconnecting:', error);
        }
    }
}

/**
 * WebSocket Subscription Module
 *
 * This module provides real-time data subscriptions using WebSocket v2 protocol
 * with multiplexed connections (one connection per client, multiple subscriptions).
 *
 * The external API remains unchanged - just use subscribe(path, options) as before.
 */
/**
 * Subscribe to real-time updates for a path.
 *
 * @param path - The path to subscribe to (e.g., 'users/123' or 'posts')
 * @param subscriptionOptions - Options including onData and onError callbacks
 * @returns A function to unsubscribe
 *
 * @example
 * ```typescript
 * const unsubscribe = await subscribe('users/123', {
 *   onData: (data) => console.log('User updated:', data),
 *   onError: (error) => console.error('Subscription error:', error),
 * });
 *
 * // Later, to unsubscribe:
 * await unsubscribe();
 * ```
 */
async function subscribe(path, subscriptionOptions) {
    return subscribeV2(path, subscriptionOptions);
}
/**
 * Close all active subscriptions.
 * Call this when cleaning up (e.g., on logout or component unmount).
 */
async function closeAllSubscriptions() {
    return closeAllSubscriptionsV2();
}
/**
 * Clear the subscription cache.
 *
 * @param path - Optional path to clear. If not provided, clears all cached data.
 */
function clearCache(path) {
    clearCacheV2(path);
}
/**
 * Get cached data for a path without making a network request.
 *
 * @param path - The path to get cached data for
 * @param prompt - Optional prompt that was used for the subscription
 * @returns The cached data, or null if not cached or expired
 */
function getCachedData(path, prompt) {
    return getCachedDataV2(path, prompt);
}
/**
 * Reconnect all WebSocket connections with fresh authentication.
 * Call this when the user's auth state changes (login/logout).
 *
 * This will:
 * 1. Close existing connections
 * 2. Re-establish with new auth token
 * 3. Re-subscribe to all active paths
 *
 * Existing subscriptions will receive new initial data after reconnection.
 */
async function reconnectWithNewAuth() {
    return reconnectWithNewAuthV2();
}

let _config = null;
class ReactNativeSessionManager {
    /**
     * Must be called once before any other method.
     *
     * ```ts
     * import { ReactNativeSessionManager } from '@pooflabs/core';
     * import { MMKV } from 'react-native-mmkv';
     * import { decode } from 'base-64';
     *
     * const mmkv = new MMKV();
     * ReactNativeSessionManager.configure({
     *   storage: {
     *     getItem: (k) => mmkv.getString(k) ?? null,
     *     setItem: (k, v) => mmkv.set(k, v),
     *     removeItem: (k) => mmkv.delete(k),
     *   },
     *   atob: decode,
     * });
     * ```
     */
    static configure(cfg) {
        _config = cfg;
    }
    static getStorage() {
        if (!_config) {
            throw new Error("ReactNativeSessionManager.configure() must be called before using session methods.");
        }
        return _config.storage;
    }
    static decodeBase64(input) {
        if (!_config) {
            throw new Error("ReactNativeSessionManager.configure() must be called before using session methods.");
        }
        return _config.atob(input);
    }
    /**
     * Decode a base64url-encoded string (used by JWT payloads).
     * Normalises base64url → standard base64 before decoding.
     */
    static decodeBase64Url(input) {
        let b64 = input.replace(/-/g, '+').replace(/_/g, '/');
        const pad = b64.length % 4;
        if (pad === 2)
            b64 += '==';
        else if (pad === 3)
            b64 += '=';
        return this.decodeBase64(b64);
    }
    /* ------------------------------------------------------------------ */
    /* STORE                                                              */
    /* ------------------------------------------------------------------ */
    static async storeSession(address, accessToken, idToken, refreshToken) {
        // JWT-wallet binding: refuse to store a session whose idToken is bound
        // to a different wallet than `address`. Prevents races that would otherwise
        // leave storage with mismatched address/token state.
        try {
            const payloadB64 = idToken.split(".")[1];
            if (payloadB64) {
                const payload = JSON.parse(this.decodeBase64Url(payloadB64));
                const tokenWallet = payload["custom:walletAddress"];
                if (tokenWallet && tokenWallet !== address) {
                    throw new Error(`[ReactNativeSessionManager] Refusing to store session: address (${address}) does not match idToken custom:walletAddress (${tokenWallet})`);
                }
                if (!tokenWallet) {
                    console.warn("[ReactNativeSessionManager] storeSession: idToken has no custom:walletAddress claim — writing without validation");
                }
            }
        }
        catch (err) {
            if (typeof (err === null || err === void 0 ? void 0 : err.message) === "string" && err.message.includes("Refusing to store session")) {
                throw err;
            }
            console.warn("[ReactNativeSessionManager] storeSession: failed to decode idToken for validation:", err);
        }
        const config = await getConfig();
        const currentAppId = config.appId;
        this.getStorage().setItem(this.TAROBASE_SESSION_STORAGE_KEY, JSON.stringify({
            address,
            accessToken,
            idToken,
            refreshToken,
            appId: currentAppId,
        }));
    }
    /* ------------------------------------------------------------------ */
    /* GET                                                                */
    /* ------------------------------------------------------------------ */
    static async getSession() {
        const session = this.getStorage().getItem(this.TAROBASE_SESSION_STORAGE_KEY);
        if (!session)
            return null;
        const sessionObj = JSON.parse(session);
        /* ---------- validate app-id ---------- */
        const config = await getConfig();
        if (sessionObj.appId && sessionObj.appId !== config.appId) {
            this.clearSession();
            return null;
        }
        /* ---------- check JWT expiration ---------- */
        try {
            const { accessToken } = sessionObj;
            const { exp } = JSON.parse(this.decodeBase64Url(accessToken.split(".")[1]));
            if (Date.now() > exp * 1000) {
                const { refreshToken } = sessionObj;
                if (!refreshToken)
                    return null;
                const refreshed = await refreshSession(refreshToken);
                if ((refreshed === null || refreshed === void 0 ? void 0 : refreshed.idToken) && (refreshed === null || refreshed === void 0 ? void 0 : refreshed.accessToken)) {
                    await this.updateIdTokenAndAccessToken(refreshed.idToken, refreshed.accessToken);
                    const newSession = this.getStorage().getItem(this.TAROBASE_SESSION_STORAGE_KEY);
                    if (!newSession)
                        return null;
                    const newObj = JSON.parse(newSession);
                    return { address: newObj.address, session: newObj };
                }
                // Refresh failed — clear stale session to prevent retry loops
                this.clearSession();
                return null;
            }
        }
        catch (err) {
            // Token decode or refresh failed — clear stale session to prevent retry loops
            this.clearSession();
            return null;
        }
        return { address: sessionObj.address, session: sessionObj };
    }
    /* ------------------------------------------------------------------ */
    /* CLEAR                                                              */
    /* ------------------------------------------------------------------ */
    static clearSession() {
        this.getStorage().removeItem(this.TAROBASE_SESSION_STORAGE_KEY);
    }
    /* ------------------------------------------------------------------ */
    /* IS-AUTH                                                            */
    /* ------------------------------------------------------------------ */
    static isAuthenticated() {
        return !!this.getStorage().getItem(this.TAROBASE_SESSION_STORAGE_KEY);
    }
    /* ------------------------------------------------------------------ */
    /* TOKEN HELPERS                                                      */
    /* ------------------------------------------------------------------ */
    static getIdToken() {
        try {
            const session = this.getStorage().getItem(this.TAROBASE_SESSION_STORAGE_KEY);
            return session ? JSON.parse(session).idToken : null;
        }
        catch (_a) {
            return null;
        }
    }
    static getRefreshToken() {
        try {
            const session = this.getStorage().getItem(this.TAROBASE_SESSION_STORAGE_KEY);
            return session ? JSON.parse(session).refreshToken : null;
        }
        catch (_a) {
            return null;
        }
    }
    /* ------------------------------------------------------------------ */
    /* UPDATE TOKENS                                                      */
    /* ------------------------------------------------------------------ */
    static async updateIdTokenAndAccessToken(idToken, accessToken) {
        var _a;
        const session = this.getStorage().getItem(this.TAROBASE_SESSION_STORAGE_KEY);
        if (!session)
            return;
        const sessionObj = JSON.parse(session);
        /* ---------- app-id guard ---------- */
        const config = await getConfig();
        if (sessionObj.appId && sessionObj.appId !== config.appId) {
            this.clearSession();
            return;
        }
        sessionObj.idToken = idToken;
        sessionObj.accessToken = accessToken;
        (_a = sessionObj.appId) !== null && _a !== void 0 ? _a : (sessionObj.appId = config.appId);
        this.getStorage().setItem(this.TAROBASE_SESSION_STORAGE_KEY, JSON.stringify(sessionObj));
    }
}
ReactNativeSessionManager.TAROBASE_SESSION_STORAGE_KEY = "tarobase_session_storage";

export { InsufficientBalanceError, ReactNativeSessionManager, ServerSessionManager, WebSessionManager, aggregate, buildSetDocumentsTransaction, clearCache, closeAllSubscriptions, convertRemainingAccounts, count, createSessionWithPrivy, createSessionWithSignature, genAuthNonce, genSolanaMessage, get, getCachedData, getConfig, getFiles, getIdToken, getMany, init, reconnectWithNewAuth, refreshSession, runExpression, runExpressionMany, runQuery, runQueryMany, set, setFile, setMany, signAndSubmitTransaction, signMessage, signSessionCreateMessage, signTransaction, subscribe };
//# sourceMappingURL=index.mjs.map
